import {
  CFDFile,
  Coordinate,
  FollowingCar,
  MiscellaneousParameterList,
  PtapLocationSet,
  RHRepoSet,
  RunConfigCase,
  SystemCode
} from '@aero-platform/shared'
import { Accordion, AccordionTab } from 'primereact/accordion'
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { FileUpload } from 'primereact/fileupload'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { SelectButton } from 'primereact/selectbutton'
import { classNames } from 'primereact/utils'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { useRecoilValue } from 'recoil'
import { useGetGeometries } from '../../api/geometry-API'
import { useGetPtapLocationSets } from '../../api/ptaplocation-set-API'
import { useGetRHRepoSets } from '../../api/rhrepo-set-API'
import { useGetSubmission, useGetSubmissions } from '../../api/submission-API'
import { protoService } from '../../proto/ProtoService'
import {
  AttitudeSet,
  BasicNameValue,
  CarCoordinate,
  DomainSet,
  Geometry,
  MethodologySet,
  Project,
  RunConfig,
  RunConfigProto,
  RunStatus,
  SimulationSet,
  StarCCMConfig,
  StarCCMParameterList,
  Trajectory,
  TyreSet,
  WorkflowSubmission,
  WorkflowSubmissionTemplate
} from '../../types/types'
import {
  cloneAttitudeSet,
  cloneDomainSet,
  cloneMethodologySet,
  clonePtapLocationSet,
  cloneRHRepoSet,
  cloneRunConfigCase,
  cloneSimulationSet,
  cloneStarCCMConfig,
  cloneTrajectory,
  cloneTyreSet,
  cloneWorkflowSubmissionTemplate,
  queryString2JsonString
} from '../../types/util'
import {
  ComplianceDescOptions,
  ComplianceTeamOptions,
  METHODOLOGY_STATUS_RELEASED,
  NASCARTeams,
  simpleRunProjection,
  UploadTypes
} from '../../utils/constants'
import {
  attitudeSetListState,
  currentGMIDState,
  currentTeamState,
  currentUserState,
  currentVehicleState,
  domainSetListState,
  isF1TeamState,
  methodologySetListState,
  simulationSetListState,
  tyreSetListState,
  workflowTemplateListState
} from '../../utils/state-atoms'
import { DropdownStyle } from '../../utils/styling-constants'
import { getDuplicableCases, isDraftingRunConfig } from '../../utils/draftingPairs'
import { CopyIcon, FileEditIcon, LoadingIcon, SaveIcon } from '../Icons'
import Modal from '../Modal'
import CloseButton from '../buttons/CloseButton'
import Upload from '../upload/Upload'
import SimTemplateDropdown from './SimTemplateDropdown'
import CaseCreatorModal, { CaseCreatorHandle } from './CaseCreator'
import { getFollowingCarCoordinates } from './DataTableBodyTemplate'
import { isPerCaseUpload } from '../../utils/simTemplateOverride'
import StarCCMRunConfigForm from './StarCCMRunConfigForm'
import TrailingCarScatterPlot from './TrailingCarScatterPlot'

type UploadFileData = {
  name: string
  path: string
  rawFile?: File
  progress?: number // 0-100
  uploading?: boolean
  failed?: boolean
  abortController?: AbortController // For managing individual file upload cancellation
}

type ParsedCoordinateRow = {
  x?: number
  y?: number
  z?: number
}

type SubmissionModalProps = {
  mode: string
  submitting: boolean
  project: Project
  latestRuns?: WorkflowSubmission[]
  existingRun?: WorkflowSubmission
  onConfirm: (
    runConfig: RunConfigProto,
    submission: WorkflowSubmission,
    aeroTempRunId?: string | null,
    gmid?: string,
    /** Defined when the sim file is still uploading and save should defer the blob copy */
    simUploadPromise?: Promise<any>,
    /**
     * When defined, the parent should call this with a callback that will be invoked
     * on every upload progress tick (0-100), even after this modal unmounts.
     */
    setupSimProgress?: (cb: (pct: number) => void) => void,
    /** Defined when geo files are still uploading and save should defer the blob copy */
    geoUploadPromise?: Promise<any>,
    setupGeoProgress?: (cb: (pct: number) => void) => void,
    /** Optional manual override for the run sequence number (XXX in R001) */
    sequenceNumberOverride?: number | null,
    /** Full source blob URL of a dropdown-selected override template to copy into the run folder */
    simOverrideSourcePath?: string | null
  ) => void
  onCancel: () => void
}

const editorTitle = {
  new: 'Create New Run',
  edit: 'Edit Run'
}

const geoStagingRoot = '/geom_changes'
const simOverrideRoot = '/staging_overrides'

const SubmissionModal: FC<SubmissionModalProps> = ({
  mode,
  submitting,
  project,
  latestRuns,
  existingRun,
  onConfirm,
  onCancel
}) => {
  const {
    control,
    setValue,
    getValues,
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<RunConfig>()

  // Register project field (hidden - set programmatically)
  register('project')

  // Cleanup temp run ID on unmount or page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (mode === 'new') {
        localStorage.removeItem('aero_temp_run_id')
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    // Load temp run ID from localStorage on mount for new mode
    if (mode === 'new') {
      const storedTempRunId = localStorage.getItem('aero_temp_run_id')
      if (storedTempRunId) {
        setAeroTempRunId(storedTempRunId)
      }
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      // Also cleanup on unmount (when modal is closed)
      if (mode === 'new') {
        localStorage.removeItem('aero_temp_run_id')
      }
    }
  }, [mode])
  const isF1Team = useRecoilValue(isF1TeamState)
  const currentTeam = useRecoilValue(currentTeamState)
  const currentUser = useRecoilValue(currentUserState)
  const currentVehicle = useRecoilValue(currentVehicleState)

  // aero-engineers may only use Released methodologies (or legacy ones with no status yet);
  // admin/developer/methodology-specialist may use any status.
  const canUseMethodologySet = useCallback(
    (item?: { status?: string }): boolean => {
      if (!item) return false
      const privilegedRoles = ['admin', 'developer', 'methodology-specialist']
      if (currentUser && privilegedRoles.includes(currentUser.role?.roleId)) return true
      return !item.status || item.status === METHODOLOGY_STATUS_RELEASED
    },
    [currentUser]
  )

  const methodologySets = useRecoilValue(methodologySetListState)
  const templateList = useRecoilValue(workflowTemplateListState)
  const attitudeSets = useRecoilValue(attitudeSetListState)

  const filteredTemplateList = useMemo(() => {
    return templateList ?? []
  }, [templateList])
  const domainSets = useRecoilValue(domainSetListState)
  const tyreSets = useRecoilValue(tyreSetListState)
  const simulationSets = useRecoilValue(simulationSetListState)
  const gmid = useRecoilValue(currentGMIDState)

  const [attitudeSetList, setAttitudeSetList] = useState<AttitudeSet[]>(attitudeSets || [])
  const [domainSetList, setDomainSetList] = useState<DomainSet[]>(domainSets || [])
  const [tyreSetList, setTyreSetList] = useState<TyreSet[]>(tyreSets || [])
  const [simulationSetList, setSimulationSetList] = useState<SimulationSet[]>(simulationSets || [])
  const [trajectoryList, setTrajectoryList] = useState<Trajectory[]>([])
  const [methodologySetList, setMethodologySetList] = useState<MethodologySet[]>(
    methodologySets || []
  )

  // Derive from the watched form field rather than dropdown-only state: project defaults, existing
  // runs and duplicated runs populate selectedMethodSet via setValue (no dropdown onChange), so a
  // separate setter would leave the override limits / divisibility validation unset on those paths.
  const selectedMethodologySet = watch('selectedMethodSet') as MethodologySet | undefined
  // Override presets/limits come from the StarCCM system specifically. MethodologySets stores
  // systems in ANSA → StarCCM → EnSight order, so index 0 can be ANSA (max cores 0); resolve by name.
  const starccmSystem = selectedMethodologySet?.selectedSystems?.find(s => s?.name === 'starccmp')

  // Keep the attitude-set options in sync with the global list so newly created
  // attitude sets appear without a page refresh, preserving any run-specific set
  // that isn't part of the global list.
  useEffect(() => {
    setAttitudeSetList(prev => {
      const extras = prev.filter(p => !(attitudeSets || []).some(a => a._id === p._id))
      return [...(attitudeSets || []), ...extras]
    })
  }, [attitudeSets])

  const [projectList] = useState<Project[]>([project])
  const [runFilterFor, setRunFilterFor] = useState<string>('')
  const [geoRuns, setGeoRuns] = useState<WorkflowSubmission[]>(latestRuns || [])
  const [refRuns, setRefRuns] = useState<WorkflowSubmission[]>(latestRuns || [])
  const [dupeRuns, setDupeRuns] = useState<WorkflowSubmission[]>(latestRuns || [])
  const [dupeRun, setDupeRun] = useState<WorkflowSubmission>()
  const [{ data: runData, loading: loadingRun }, fetchRuns] = useGetSubmissions()
  const [{ loading: loadingOneRun }, fetchOneRun] = useGetSubmission()
  const [, fetchGeometry] = useGetGeometries()
  const [{ loading: loadingPtap }, fetchPtapLocationSets] = useGetPtapLocationSets()
  const [{ loading: loadingRHRepo }, fetchRHRepoSets] = useGetRHRepoSets()
  const [geometryList, setGeometryList] = useState<Geometry[]>([])
  const [ptapLocationSetList, setPtapLocationSetList] = useState<PtapLocationSet[]>([])
  const [rhRepoSetList, setRhRepoSetList] = useState<RHRepoSet[]>([])
  const [selectedGeoFiles, setSelectedGeoFiles] = useState<UploadFileData[]>([])
  const [selectedGeometryChangeImage, setSelectedGeometryChangeImage] = useState<UploadFileData[]>(
    []
  )
  const [selectedSimFile, setSelectedSimFile] = useState<UploadFileData[]>([])
  // Sim template inherited from a duplicated run: not applied to the new run, but made
  // available as a selectable option in the Override Template Sim File dropdown.
  const [duplicatedSimTemplate, setDuplicatedSimTemplate] = useState<CFDFile | null>(null)
  const [aeroTempRunId, setAeroTempRunId] = useState<string | null>(null)
  const [showSimBackgroundModal, setShowSimBackgroundModal] = useState(false)
  const [showGeoBackgroundModal, setShowGeoBackgroundModal] = useState(false)
  const [showCloseConfirmModal, setShowCloseConfirmModal] = useState(false)
  const [isSubmittingBackground, setIsSubmittingBackground] = useState(false)
  // Set to true just before triggering react-hook-form's handleSubmit so onSubmit knows
  // to pass the upload promise through to the parent for background tracking.
  const saveSimInBackgroundRef = useRef(false)
  const saveGeoInBackgroundRef = useRef(false)
  // Holds the raw upload Promise exposed by the Override Sim File <Upload> component.
  const simUploadPromiseRef = useRef<Promise<any> | null>(null)
  // Full source blob URL of a dropdown-selected override template (not a fresh upload). The
  // server copies it into this run's own staging_overrides folder on save. Null when the override
  // was cleared, is the methodology default, or came from a fresh .sim upload.
  const overrideSimSourceUrlRef = useRef<string | null>(null)
  // Holds raw upload Promises exposed by geometry-related <Upload> components.
  const geoUploadPromisesRef = useRef<Promise<any>[]>([])
  // Stable plain-object hook: survives modal unmount so Azure SDK progress callbacks
  // can still route to the store after the user dismissed the modal.
  const simProgressHook = useRef<{ onProgress: (pct: number) => void }>({ onProgress: () => {} })
  const geoProgressHook = useRef<{ onProgress: (pct: number) => void }>({ onProgress: () => {} })

  const registerGeoUploadPromise = (promise: Promise<any>) => {
    geoUploadPromisesRef.current = [...geoUploadPromisesRef.current, promise]
  }

  const normalizeGeometryChangeImages = (images?: string | string[]) => {
    if (Array.isArray(images)) return images
    return images ? [images] : []
  }

  const getGeoUploadPromise = () => {
    if (geoUploadPromisesRef.current.length === 0) return undefined
    if (geoUploadPromisesRef.current.length === 1) return geoUploadPromisesRef.current[0]
    return Promise.all(geoUploadPromisesRef.current)
  }

  // StarCCM configuration state
  const [selectedMacroSteps, setSelectedMacroSteps] = useState<BasicNameValue[]>([])
  const [selectedStarCCMPreset, setSelectedStarCCMPreset] = useState<StarCCMParameterList>()
  const [selectedMiscPreset, setSelectedMiscPreset] = useState<MiscellaneousParameterList>()
  const refStarCCMConfig = useRef<StarCCMConfig | undefined>(undefined)
  const caseCreatorRef = useRef<CaseCreatorHandle>(null)

  const [coordinatesInputMode, setCoordinatesInputMode] = useState('manual')
  const [coordinates, setCoordinates] = useState<CarCoordinate[]>([])
  const [coordinatesFromCSV, setCoordinatesFromCSV] = useState<CarCoordinate[]>([])
  const [newCoordinate, setNewCoordinate] = useState<CarCoordinate>({ x: undefined, y: undefined })
  const newCoordinateXInputRef = useRef<HTMLInputElement | null>(null)
  const newCoordinateYInputRef = useRef<HTMLInputElement | null>(null)
  const [selectedCoordinatesFile, setSelectedCoordinatesFile] = useState<File | null>(null)
  const [parsedCoordinatesRows, setParsedCoordinatesRows] = useState<ParsedCoordinateRow[]>([])

  // Optional sequence number override for the run identifier (XXX in R001, R002)
  const [sequenceNumberOverride, setSequenceNumberOverride] = useState<number | null>(null)

  const coordinatesEditable =
    mode === 'new' || (mode === 'edit' && existingRun?.overallStatus === RunStatus.CREATED)

  const parseInputNumberValue = (value: string) => {
    const normalizedValue = value.replaceAll(',', '').trim()

    if (!normalizedValue) return undefined

    const parsedValue = Number(normalizedValue)
    return Number.isFinite(parsedValue) ? parsedValue : undefined
  }

  const addManualCoordinate = (overrideY?: number) => {
    const nextY = overrideY ?? newCoordinate.y

    if (newCoordinate.x === undefined || nextY === undefined) {
      toast.error('Please enter both X and Y values before adding a coordinate.')
      return
    }

    setCoordinates([...coordinates, { x: newCoordinate.x, y: nextY }])
    setNewCoordinate({ x: undefined, y: undefined })
    requestAnimationFrame(() => {
      if (newCoordinateXInputRef.current) {
        newCoordinateXInputRef.current.value = ''
      }
      if (newCoordinateYInputRef.current) {
        newCoordinateYInputRef.current.value = ''
      }
      newCoordinateXInputRef.current?.focus()
    })
  }

  const parseCoordinateCsv = (csvText: string): ParsedCoordinateRow[] => {
    const lines = csvText
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean)

    if (lines.length === 0) return []

    const toColumns = (line: string) => line.split(',').map(col => col.trim())
    const toNumber = (value?: string) => {
      if (!value) return undefined
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : undefined
    }

    const firstColumns = toColumns(lines[0])
    const headerMap = firstColumns.map(col => col.toLowerCase())
    const hasHeader = headerMap.some(col => ['x', 'y', 'z'].includes(col))

    const rows = hasHeader ? lines.slice(1) : lines

    return rows
      .map(line => {
        const cols = toColumns(line)

        if (hasHeader) {
          const xIndex = headerMap.indexOf('x')
          const yIndex = headerMap.indexOf('y')
          const zIndex = headerMap.indexOf('z')

          const row = {
            x: xIndex >= 0 ? toNumber(cols[xIndex]) : undefined,
            y: yIndex >= 0 ? toNumber(cols[yIndex]) : undefined,
            z: zIndex >= 0 ? toNumber(cols[zIndex]) : undefined
          }

          if (row.x === undefined && row.y === undefined && row.z === undefined) return null
          return row
        }

        const row = {
          x: toNumber(cols[0]),
          y: toNumber(cols[1]),
          z: toNumber(cols[2])
        }

        if (row.x === undefined && row.y === undefined && row.z === undefined) return null

        return row
      })
      .filter(row => row !== null)
  }

  // Compute customMacros from the selected methodology set's systemCodeFiles
  const customMacros = useMemo(() => {
    const formValues = control._formValues as RunConfig
    const methodSet = formValues?.selectedMethodSet
    if (!methodSet?.systemCodeFiles) return []

    const macroSet = new Set<string>()
    methodSet.systemCodeFiles
      .filter((file: SystemCode) => file.fileRef?.fileType === 'code')
      .forEach((file: SystemCode) => {
        if (file.fileRef?.file) {
          macroSet.add(file.fileRef.file)
        }
      })
    return Array.from(macroSet).sort()
  }, [control._formValues])

  // StarCCM template options for the run-level Override picker (new runs use these directly;
  // existing runs let SimTemplateDropdown fetch from the server). Keyed on the watched method set
  // so it recomputes when the methodology loads/changes.
  const watchedMethodSetForTemplates = watch('selectedMethodSet')
  const methodologyTemplates = useMemo(() => {
    const templates: CFDFile[] = []
    const methodSet = watchedMethodSetForTemplates
    if (methodSet?.systemCodeFiles) {
      methodSet.systemCodeFiles
        .filter(
          (f: SystemCode) => f.fileRef?.fileType === 'template' && f.fileRef?.system === 'starccm'
        )
        .forEach((f: SystemCode) => {
          if (f.fileRef) templates.push(f.fileRef as CFDFile)
        })
    }
    return templates
  }, [watchedMethodSetForTemplates])

  const labelRequiredByF1Team = classNames('mb-2', { 'text-gray-400': !isF1Team })

  // Cleanup temp run ID on unmount or page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (mode === 'new') {
        localStorage.removeItem('aero_temp_run_id')
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    // Load temp run ID from localStorage on mount for new mode
    if (mode === 'new') {
      const storedTempRunId = localStorage.getItem('aero_temp_run_id')
      if (storedTempRunId) {
        setAeroTempRunId(storedTempRunId)
      }
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      // Also cleanup on unmount (when modal is closed)
      if (mode === 'new') {
        localStorage.removeItem('aero_temp_run_id')
      }
    }
  }, [mode])

  useEffect(() => {
    let setRunsFunc
    if (runFilterFor === 'geo') {
      setRunsFunc = setGeoRuns
    } else if (runFilterFor === 'dup') {
      setRunsFunc = setDupeRuns
    } else if (runFilterFor === 'ref') {
      setRunsFunc = setRefRuns
    }
    if (runData && runData.protoBytesList.length) {
      const submissions = protoService.decodeProtoResponse(runData, 'workflowsubmission')
      setRunsFunc &&
        setRunsFunc(
          (submissions as WorkflowSubmission[]).map(item => {
            return item
          })
        )
      // runFilterFor === 'dup' && setRuns(submissions)
    } else {
      /**
       * this is supposed to set a empty array, because a bug of filter resetting,
       * we have to use this for now
       */
      console.log('no runs found')
      setRunsFunc &&
        setRunsFunc([
          {
            _id: '',
            name: ' '
            // name: 'No runs found'
          } as WorkflowSubmission
        ])
      // runFilterFor === 'dup' && setRuns([])
    }
  }, [runData, runFilterFor])

  const complianceDesc = watch('complianceDesc')
  const watchedOverrideSimFile = watch('overrideSelectedSimFile')
  const watchedSimOverrideDir = watch('simOverrideDir')

  // Run-level Override picker: methodology default (display only) when no override is set,
  // otherwise the currently-selected override file.
  const overrideDropdownValue: CFDFile | null = watchedOverrideSimFile
    ? ({
        file: watchedOverrideSimFile,
        path: watchedSimOverrideDir || '',
        system: 'starccm',
        fileType: 'template',
        rootType: 'override'
      } as CFDFile)
    : methodologyTemplates[0] || null

  const stripToContainerRelative = (path: string): string => {
    if (path?.startsWith('https://')) {
      try {
        const url = new URL(path)
        const prefix = `/cfd-cases/${currentTeam}`
        if (url.pathname.startsWith(prefix)) {
          return url.pathname.slice(prefix.length).replace(/\/$/, '')
        }
      } catch {
        /* fall back to the raw value */
      }
    }
    return (path || '').replace(/\/$/, '')
  }

  // Picking the methodology template = no run-level override (cases fall back to the per-case
  // default). Picking any other template sets the run-level override, mirroring ReplaceSimFileModal.
  const handlePickOverrideTemplate = (tpl: CFDFile | null) => {
    const isMethodologyDefault = !tpl || (tpl.rootType || '').startsWith('methodolog')
    if (isMethodologyDefault) {
      // Leave simOverrideDir intact (it stages uploads); override is inert while the file is empty.
      setValue('overrideSelectedSimFile', '')
      setSelectedSimFile([])
      overrideSimSourceUrlRef.current = null
      return
    }
    setValue('overrideSelectedSimFile', tpl!.file || '')
    setValue('simOverrideDir', stripToContainerRelative(tpl!.path || ''))
    // Remember the template's real source blob so the server can copy it into this run's folder.
    const dir = (tpl!.path || '').replace(/\/$/, '')
    overrideSimSourceUrlRef.current = dir && tpl!.file ? `${dir}/${tpl!.file}` : null
    setSelectedSimFile([
      {
        name: tpl!.file || '',
        path: tpl!.path || '',
        progress: 100,
        uploading: false,
        failed: false,
        abortController: undefined
      }
    ])
  }

  const [currentAttitudeSet, setCurrentAttitudeSet] = useState<AttitudeSet | null>(null)
  const [currentPtapLocationSet, setCurrentPtapLocationSet] = useState<PtapLocationSet | null>(null)
  const [currentRHRepoSet, setCurrentRHRepoSet] = useState<RHRepoSet | null>(null)
  const [masterGeoTemplates, setMasterGeoTemplates] = useState<any[]>([])
  const [casesExpanded, setCasesExpanded] = useState(false)

  useEffect(() => {
    const proj = projectList.find(item => item._id === project._id)
    setValue('project', proj)

    fetchGeometry()
      .then(res => {
        if (res && res.data && res.data.protoBytesList.length) {
          const geometries = protoService.decodeProtoResponse(res.data, 'geometry')
          setGeometryList(geometries)
        }
      })
      .catch(err => {
        console.error(err)
        toast.error('Error loading geometries.')
      })

    fetchPtapLocationSets()
      .then(res => {
        if (res && res.data && res.data.protoBytesList.length) {
          const ptapLocationSets = protoService.decodeProtoResponse(res.data, 'ptaplocationset')
          setPtapLocationSetList(ptapLocationSets)
          if (mode === 'edit' && existingRun?.runConfig?.selectedPtapLocationSet) {
            const locationSet = ptapLocationSets.find(
              item => item._id === existingRun.runConfig.selectedPtapLocationSet._id
            )
            if (locationSet) {
              setValue('selectedPtapLocationSet', locationSet)
              setCurrentPtapLocationSet(locationSet)
            }
          }
        }
      })
      .catch(err => {
        console.error(err)
        toast.error('Error loading PTAP location sets.')
      })

    fetchRHRepoSets()
      .then(res => {
        if (res && res.data && res.data.protoBytesList.length) {
          const rhRepoSets = protoService.decodeProtoResponse(res.data, 'rhreposet')
          setRhRepoSetList(rhRepoSets)
          if (mode === 'edit' && existingRun?.runConfig?.selectedRHRepoSet) {
            const repoSet = rhRepoSets.find(
              item => item._id === existingRun.runConfig.selectedRHRepoSet._id
            )
            if (repoSet) {
              setValue('selectedRHRepoSet', repoSet)
              setCurrentRHRepoSet(repoSet)
            }
          }
          // Auto-populate RH Repo Set for new runs once data is available
          if (mode === 'new' && project.attitudeSet?.name) {
            const matchedRHRepo = rhRepoSets.find(rh => rh.name === project.attitudeSet.name)
            if (matchedRHRepo) {
              setValue('selectedRHRepoSet', matchedRHRepo)
              setCurrentRHRepoSet(matchedRHRepo)
            }
          }
        }
      })
      .catch(err => {
        console.error(err)
        toast.error('Error loading RHRepo sets.')
      })

    if (mode === 'new') {
      if (project.submissionTemplateObj && project.submissionTemplateObj._id) {
        setValue('template', project.submissionTemplateObj)
      }

      if (project.attitudeSet && project.attitudeSet._id) {
        const selectedAttitudeSet = attitudeSetList.find(
          item => item._id === project.attitudeSet._id
        )
        if (selectedAttitudeSet) {
          setValue('selectedAttitudeSet', selectedAttitudeSet)
          setCurrentAttitudeSet(selectedAttitudeSet)
          setTrajectoryList(selectedAttitudeSet.trajectories || [])
          // fetchVehicleStates(`query=${queryString2JsonString('sequenceId=' + selectedSeq.name)}`)
        } else {
          setAttitudeSetList([...attitudeSetList, project.attitudeSet])
          setValue('selectedAttitudeSet', project.attitudeSet)
          setCurrentAttitudeSet(project.attitudeSet)
          setTrajectoryList(project.attitudeSet.trajectories || [])
        }
      }

      // Only seed the project's default methodology when the user is allowed to use it,
      // otherwise a non-Released default would silently become submittable.
      if (project.methodSet && project.methodSet._id && canUseMethodologySet(project.methodSet)) {
        const selectedMethodSet = methodologySetList.find(
          item => item._id === project.methodSet._id
        )
        if (selectedMethodSet) {
          setValue('selectedMethodSet', selectedMethodSet)
        } else {
          setMethodologySetList([...methodologySetList, project.methodSet])
          setValue('selectedMethodSet', project.methodSet)
        }
      }

      if (isF1Team && project.domainSet && project.domainSet._id) {
        const selectedDomainSet = domainSetList.find(item => item._id === project.domainSet._id)
        if (selectedDomainSet) {
          setValue('selectedDomainSet', selectedDomainSet)
        } else {
          setDomainSetList([...domainSetList, project.domainSet])
          setValue('selectedDomainSet', project.domainSet)
        }
      }

      if (isF1Team && project.tyreSet && project.tyreSet._id) {
        const selectedTyreSet = tyreSetList.find(item => item._id === project.tyreSet._id)
        if (selectedTyreSet) {
          setValue('selectedTyreSet', selectedTyreSet)
        } else {
          setTyreSetList([...tyreSetList, project.tyreSet])
          setValue('selectedTyreSet', project.tyreSet)
        }
      }

      if (isF1Team && project.simulationSet && project.simulationSet._id) {
        const selectedSimulationSet = simulationSetList.find(
          item => item._id === project.simulationSet._id
        )
        if (selectedSimulationSet) {
          setValue('selectedSimulationSet', selectedSimulationSet)
        } else {
          setSimulationSetList([...simulationSetList, project.simulationSet])
          setValue('selectedSimulationSet', project.simulationSet)
        }
      }

      // Skip name-shaped ids (id === name) that indicate a corrupted project.referenceRun
      if (project.referenceRunName && project.referenceRun !== project.referenceRunName) {
        setValue('referenceRunId', project.referenceRun)
        setValue('referenceRunName', project.referenceRunName)
        if (!refRuns.find(item => item._id === project.referenceRun)) {
          const tmpRun = {
            _id: project.referenceRun,
            name: project.referenceRunName
          } as WorkflowSubmission
          setRefRuns([...refRuns, tmpRun])
        }
      }

      if (project.baselineRunName) {
        setValue('baselineRunId', project.baselineRunId)
        setValue('baselineRunName', project.baselineRunName)
        if (!geoRuns.find(item => item.name === project.baselineRunName)) {
          const tmpRun = {
            _id: project.baselineRunId,
            name: project.baselineRunName
          } as WorkflowSubmission
          setGeoRuns([...geoRuns, tmpRun])
        }
      }

      if (project.area === 'MC') {
        setValue('complianceDesc', 'Drafting')
      }

      if (!isF1Team) {
        setValue('geoStagingDir', `${geoStagingRoot}/${gmid}`)
        // For new runs, simOverrideDir will be set when file is uploaded
        // Path will be: /staging_overrides/{gmid} (aeroTempRunId added during upload)
        if (existingRun?.name) {
          setValue('simOverrideDir', `${simOverrideRoot}/${gmid}/${existingRun.name}`)
        } else {
          setValue('simOverrideDir', `${simOverrideRoot}/${gmid}`)
        }
      }
    } else if (mode === 'edit' && existingRun) {
      if (existingRun?.templateOriginId) {
        const tmpTemplate = templateList.find(item => item._id === existingRun.templateOriginId)
        setValue('template', tmpTemplate as WorkflowSubmissionTemplate)
      }

      const existingConfig = existingRun.runConfig
      isF1Team && setValue('RATGstatus', existingConfig.RATGstatus)

      setValue('coresCount', existingConfig?.coresCount)
      setValue('walltimeOverride', existingConfig?.walltimeOverride)

      if (existingConfig.selectedAttitudeSet && existingConfig.selectedAttitudeSet._id) {
        const selectedAttitudeSet = attitudeSetList.find(
          item => item._id === existingConfig.selectedAttitudeSet._id
        )
        let trajs: Trajectory[]
        if (selectedAttitudeSet) {
          setValue('selectedAttitudeSet', selectedAttitudeSet)
          setCurrentAttitudeSet(selectedAttitudeSet)
          trajs = selectedAttitudeSet.trajectories || []
          // fetchVehicleStates(`query=${queryString2JsonString('sequenceId=' + selectedSeq.name)}`)
        } else {
          setAttitudeSetList([...attitudeSetList, existingConfig.selectedAttitudeSet])
          setValue('selectedAttitudeSet', existingConfig.selectedAttitudeSet)
          setCurrentAttitudeSet(existingConfig.selectedAttitudeSet)
          trajs = existingConfig.selectedAttitudeSet.trajectories || []
        }
        setTrajectoryList(trajs)
        if (isF1Team && existingConfig.selectedTrajectory) {
          const selectedTraj = trajs.find(
            item => item.name === existingConfig.selectedTrajectory.name
          )
          if (selectedTraj) {
            setValue('selectedTrajectory', selectedTraj)
          }
        }
        // Auto-populate RH Repo Set if not already set on the existing run
        if (!existingConfig.selectedRHRepoSet?._id) {
          const attSetName = selectedAttitudeSet?.name || existingConfig.selectedAttitudeSet.name
          const matchedRHRepo = rhRepoSetList.find(rh => rh.name === attSetName)
          if (matchedRHRepo) {
            setValue('selectedRHRepoSet', matchedRHRepo)
            setCurrentRHRepoSet(matchedRHRepo)
          }
        }
      }

      if (existingConfig.selectedMethodSet && existingConfig.selectedMethodSet._id) {
        const selectedMethodSet = methodologySetList.find(
          item => item._id === existingConfig.selectedMethodSet._id
        )
        if (selectedMethodSet) {
          // Preserve per-run starccmConfig customizations over template defaults
          const methodSetToUse = cloneMethodologySet(selectedMethodSet)
          if (existingConfig.selectedMethodSet.starccmConfig) {
            methodSetToUse.starccmConfig = cloneStarCCMConfig(
              existingConfig.selectedMethodSet.starccmConfig
            )
          }
          setValue('selectedMethodSet', methodSetToUse)
        } else {
          setMethodologySetList([...methodologySetList, existingConfig.selectedMethodSet])
          setValue('selectedMethodSet', existingConfig.selectedMethodSet)
        }
      }

      if (isF1Team && existingConfig.selectedDomainSet && existingConfig.selectedDomainSet._id) {
        const selectedDomainSet = domainSetList.find(
          item => item._id === existingConfig.selectedDomainSet._id
        )
        if (selectedDomainSet) {
          setValue('selectedDomainSet', selectedDomainSet)
        } else {
          setDomainSetList([...domainSetList, existingConfig.selectedDomainSet])
          setValue('selectedDomainSet', existingConfig.selectedDomainSet)
        }
      }

      if (isF1Team && existingConfig.selectedTyreSet && existingConfig.selectedTyreSet._id) {
        const selectedTyreSet = tyreSetList.find(
          item => item._id === existingConfig.selectedTyreSet._id
        )
        if (selectedTyreSet) {
          setValue('selectedTyreSet', selectedTyreSet)
        } else {
          setTyreSetList([...tyreSetList, existingConfig.selectedTyreSet])
          setValue('selectedTyreSet', existingConfig.selectedTyreSet)
        }
      }

      if (
        isF1Team &&
        existingConfig.selectedSimulationSet &&
        existingConfig.selectedSimulationSet._id
      ) {
        const selectedSimulationSet = simulationSetList.find(
          item => item._id === existingConfig.selectedSimulationSet._id
        )
        if (selectedSimulationSet) {
          setValue('selectedSimulationSet', selectedSimulationSet)
        } else {
          setSimulationSetList([...simulationSetList, existingConfig.selectedSimulationSet])
          setValue('selectedSimulationSet', existingConfig.selectedSimulationSet)
        }
      }

      if (!isF1Team) {
        const geoDir = existingConfig.geoStagingDir || `${geoStagingRoot}/${gmid}`
        setValue('geoStagingDir', geoDir)

        // Parse existing file paths if they exist (semicolon-separated)
        if (existingConfig.geoStagingFiles?.length > 0) {
          const files = existingConfig.geoStagingFiles
          const filesData: UploadFileData[] = files.map(file => ({
            name: file,
            path: `${geoDir}/${file}`,
            progress: 100,
            uploading: false,
            failed: false,
            abortController: undefined
          }))
          setSelectedGeoFiles(filesData)
        } else {
          setSelectedGeoFiles([])
        }

        const geometryChangeImages = normalizeGeometryChangeImages(
          existingConfig.geometryChangeImage
        )
        if (geometryChangeImages.length > 0) {
          setSelectedGeometryChangeImage(
            geometryChangeImages.map(image => ({
              name: image,
              path: `${geoDir}/${image}`,
              progress: 100,
              uploading: false,
              failed: false,
              abortController: undefined
            }))
          )
          setValue('geometryChangeImage', geometryChangeImages)
        } else {
          setSelectedGeometryChangeImage([])
        }

        // Construct proper simOverrideDir path based on Azure structure
        // Path should be: /staging_overrides/{gmid}/{runName}
        const baseSimOverrideDir = `${simOverrideRoot}/${gmid}`
        let simOverrideDir: string
        if (existingConfig.simOverrideDir) {
          // Normalize legacy values that only stored /staging_overrides/{gmid}
          if (
            existingConfig.simOverrideDir === baseSimOverrideDir ||
            existingConfig.simOverrideDir === `${baseSimOverrideDir}/`
          ) {
            simOverrideDir = `${baseSimOverrideDir}/${existingRun.name}`
          } else {
            simOverrideDir = existingConfig.simOverrideDir
          }
        } else {
          simOverrideDir = `${baseSimOverrideDir}/${existingRun.name}`
        }
        setValue('simOverrideDir', simOverrideDir)
        if (existingConfig.overrideSelectedSimFile) {
          const simFileData: UploadFileData = {
            name: existingConfig.overrideSelectedSimFile,
            path: `${simOverrideDir}/${existingConfig.overrideSelectedSimFile}`,
            progress: 100,
            uploading: false,
            failed: false,
            abortController: undefined
          }
          setSelectedSimFile([simFileData])
        } else {
          setSelectedSimFile([])
        }
      }

      // master geo init
      if (existingRun.runConfig.baselineRunId) {
        // setValue('baselineRun', existingRun.runConfig.baselineRun)
        if (!geoRuns.find(item => item._id === existingRun.runConfig.baselineRunId)) {
          const tmpRunObj = {
            _id: existingRun.runConfig.baselineRunId,
            name: existingRun.runConfig.baselineRunName
          } as WorkflowSubmission
          setGeoRuns([tmpRunObj, ...geoRuns])
        }
        setValue('baselineRunName', existingRun.runConfig.baselineRunName)
        setValue('baselineRunId', existingRun.runConfig.baselineRunId)
      }

      // ref run init
      if (existingRun.runConfig.referenceRunId) {
        // setValue('baselineRun', existingRun.runConfig.baselineRun)
        if (!refRuns.find(item => item._id === existingRun.runConfig.referenceRunId)) {
          const tmpRunObj = {
            _id: existingRun.runConfig.referenceRunId,
            name: existingRun.runConfig.referenceRunName
          } as WorkflowSubmission
          setRefRuns([tmpRunObj, ...refRuns])
        }
        setValue('referenceRunName', existingRun.runConfig.referenceRunName)
        setValue('referenceRunId', existingRun.runConfig.referenceRunId)
      }

      setValue('description', existingRun.runConfig.description)

      // Compliance fields
      if (existingRun.runConfig.complianceTeam) {
        setValue('complianceTeam', existingRun.runConfig.complianceTeam)
      }
      if (existingRun.runConfig.complianceDesc) {
        setValue('complianceDesc', existingRun.runConfig.complianceDesc)
      }

      if (existingRun.runConfig.followingCars && existingRun.runConfig.followingCars.length > 0) {
        setValue('followingCars', existingRun.runConfig.followingCars)
        const carCoordinates = existingRun.runConfig.followingCars.map((display, index) => {
          return {
            x: display.localPosition.distance_x_m,
            y: display.localPosition.distance_y_m
          } as CarCoordinate
        })
        setCoordinates(carCoordinates)
      }
    }
  }, [])

  useEffect(() => {
    if (existingRun) {
      const currentGeoStagingFiles = existingRun.runConfig?.geoStagingFiles
      if (currentGeoStagingFiles) {
        const files = currentGeoStagingFiles.map(file => ({
          name: file,
          path: `${existingRun.runConfig.geoStagingDir}/${file}`,
          progress: 100,
          uploading: false
        }))
        setSelectedGeoFiles(files)
        setValue('geoStagingFiles', currentGeoStagingFiles)
      }

      const currentGeometryChangeImages = normalizeGeometryChangeImages(
        existingRun.runConfig?.geometryChangeImage
      )
      if (currentGeometryChangeImages.length > 0) {
        const geoDir = existingRun.runConfig.geoStagingDir || `${geoStagingRoot}/${gmid}`
        setSelectedGeometryChangeImage(
          currentGeometryChangeImages.map(image => ({
            name: image,
            path: `${geoDir}/${image}`,
            progress: 100,
            uploading: false,
            failed: false
          }))
        )
        setValue('geometryChangeImage', currentGeometryChangeImages)
      }

      const currentSimOverrideFile = existingRun.runConfig?.overrideSelectedSimFile
      if (currentSimOverrideFile) {
        // Use simOverrideDir from config or construct proper path based on Azure structure
        const simOverrideDir =
          existingRun.runConfig.simOverrideDir || `${simOverrideRoot}/${gmid}/${existingRun.name}`
        const simFile = {
          name: currentSimOverrideFile,
          path: `${simOverrideDir}/${currentSimOverrideFile}`,
          progress: 100,
          uploading: false
        }
        setSelectedSimFile([simFile])
        // Populate the form field with the file name
        setValue('overrideSelectedSimFile', currentSimOverrideFile)
      }
    }
  }, [existingRun, gmid, setValue])

  // Populate StarCCM configuration when methodology set changes
  const selectedMethodSet = watch('selectedMethodSet')

  // aero-engineers may only pick Released methodologies (or legacy ones with no status yet);
  // privileged roles see every status. In edit mode the currently selected set is always kept
  // so editing a run never drops its existing methodology — but new/duplicated runs must not
  // retain an injected non-Released default, or it could be submitted despite the Released rule.
  const visibleMethodologySetList = useMemo(() => {
    return methodologySetList.filter(
      item => canUseMethodologySet(item) || (mode === 'edit' && item._id === selectedMethodSet?._id)
    )
  }, [methodologySetList, canUseMethodologySet, mode, selectedMethodSet])

  useEffect(() => {
    if (selectedMethodSet?.starccmConfig) {
      console.log(
        'Populating StarCCM config from methodology set:',
        selectedMethodSet.starccmConfig
      )

      // Set macro steps
      if (selectedMethodSet.starccmConfig.macroSteps) {
        setSelectedMacroSteps(selectedMethodSet.starccmConfig.macroSteps)
      }

      // Set StarCCM parameter preset
      if (selectedMethodSet.starccmConfig.starccmParameters) {
        setSelectedStarCCMPreset(selectedMethodSet.starccmConfig.starccmParameters)
      }

      // Set miscellaneous parameter preset
      if (selectedMethodSet.starccmConfig.miscellaneousParameters) {
        setSelectedMiscPreset(selectedMethodSet.starccmConfig.miscellaneousParameters)
      }

      // Store the full config in the ref
      refStarCCMConfig.current = selectedMethodSet.starccmConfig
    } else {
      // Reset to empty state if no config
      setSelectedMacroSteps([])
      setSelectedStarCCMPreset(undefined)
      setSelectedMiscPreset(undefined)
      refStarCCMConfig.current = undefined
    }
  }, [selectedMethodSet])

  const getFormErrorMessage = name => {
    return errors[name] ? (
      <small className="error">{errors[name]?.message}</small>
    ) : (
      <small className="error">&nbsp;</small>
    )
  }

  const handleDuplicateRun = async () => {
    if (!dupeRun) {
      return
    }

    let tmpRun = dupeRuns?.find(item => item._id === dupeRun._id)
    if (!tmpRun) {
      return
    }

    try {
      const resp = await fetchOneRun(tmpRun._id)
      if (resp && resp.data && resp.data.protoBytesList.length) {
        const submissions = protoService.decodeProtoResponse(resp.data, 'workflowsubmission')
        tmpRun = submissions[0]
      } else {
        toast.error('No run is found.')
        return
      }
    } catch (err) {
      toast.error('Error loading the run.')
      return
    }

    const existingConfig: RunConfig = tmpRun.runConfig
    const isDraftingSource = isDraftingRunConfig(existingConfig)
    setValue('RATGstatus', existingConfig.RATGstatus)
    setValue('coresCount', existingConfig.coresCount)
    setValue('walltimeOverride', existingConfig.walltimeOverride)
    if (existingConfig.selectedAttitudeSet && existingConfig.selectedAttitudeSet._id) {
      const selectedAttitudeSet = attitudeSetList.find(
        item => item._id === existingConfig.selectedAttitudeSet._id
      )
      let trajs: Trajectory[]
      if (selectedAttitudeSet) {
        setValue('selectedAttitudeSet', selectedAttitudeSet)
        setCurrentAttitudeSet(selectedAttitudeSet)
        trajs = selectedAttitudeSet.trajectories || []
      } else {
        setAttitudeSetList([...attitudeSetList, existingConfig.selectedAttitudeSet])
        setValue('selectedAttitudeSet', existingConfig.selectedAttitudeSet)
        setCurrentAttitudeSet(existingConfig.selectedAttitudeSet)
        trajs = existingConfig.selectedAttitudeSet.trajectories || []
      }
      setTrajectoryList(trajs)
      if (existingConfig.selectedTrajectory) {
        const selectedTraj = trajs.find(
          item => item.name === existingConfig.selectedTrajectory.name
        )
        if (selectedTraj) {
          setValue('selectedTrajectory', selectedTraj)
        }
      }
    }

    if (isF1Team && existingConfig.selectedDomainSet && existingConfig.selectedDomainSet._id) {
      const selectedDomainSet = domainSetList.find(
        item => item._id === existingConfig.selectedDomainSet._id
      )
      if (selectedDomainSet) {
        setValue('selectedDomainSet', selectedDomainSet)
      } else {
        setDomainSetList([...domainSetList, existingConfig.selectedDomainSet])
        setValue('selectedDomainSet', existingConfig.selectedDomainSet)
      }
    }

    if (isF1Team && existingConfig.selectedTyreSet && existingConfig.selectedTyreSet._id) {
      const selectedTyreSet = tyreSetList.find(
        item => item._id === existingConfig.selectedTyreSet._id
      )
      if (selectedTyreSet) {
        setValue('selectedTyreSet', selectedTyreSet)
      } else {
        setTyreSetList([...tyreSetList, existingConfig.selectedTyreSet])
        setValue('selectedTyreSet', existingConfig.selectedTyreSet)
      }
    }

    if (
      isF1Team &&
      existingConfig.selectedSimulationSet &&
      existingConfig.selectedSimulationSet._id
    ) {
      const selectedSimulationSet = simulationSetList.find(
        item => item._id === existingConfig.selectedSimulationSet._id
      )
      if (selectedSimulationSet) {
        setValue('selectedSimulationSet', selectedSimulationSet)
      } else {
        setSimulationSetList([...simulationSetList, existingConfig.selectedSimulationSet])
        setValue('selectedSimulationSet', existingConfig.selectedSimulationSet)
      }
    }

    if (
      !isF1Team &&
      existingConfig.selectedMasterGeometry &&
      existingConfig.selectedMasterGeometry._id
    ) {
      const selectedMasterGeometry = geometryList.find(
        item => item._id === existingConfig.selectedMasterGeometry._id
      )
      if (selectedMasterGeometry) {
        setValue('selectedMasterGeometry', selectedMasterGeometry)
      } else {
        // add selected to list
        setGeometryList([...geometryList, existingConfig.selectedMasterGeometry])
        setValue('selectedMasterGeometry', existingConfig.selectedMasterGeometry)
      }
    }

    if (existingConfig.selectedCases) {
      // @ts-ignore
      let key = new Date().getTime()
      const newCases: RunConfigCase[] = getDuplicableCases(
        existingConfig.selectedCases,
        isDraftingSource
      ).map(item => {
        return {
          ...item,
          // Don't inherit a run-level override template (it points at the source run's
          // staging_overrides path). Genuine per-case uploads (/sim-templates/) are kept.
          simTemplate: isPerCaseUpload(item.simTemplate) ? item.simTemplate : undefined,
          key: '' + key++
        } as RunConfigCase
      })

      setValue('selectedCases', newCases)
    }

    if (tmpRun?.templateOriginId) {
      const tmpTemplate = templateList.find(item => item._id === tmpRun.templateOriginId)
      setValue('template', tmpTemplate as WorkflowSubmissionTemplate)
    }

    // A duplicated run is a new run: don't inherit the source run's methodology unless the
    // user is allowed to use it, so a Draft/Archived source methodology can't be resubmitted.
    if (
      existingConfig.selectedMethodSet &&
      existingConfig.selectedMethodSet._id &&
      canUseMethodologySet(existingConfig.selectedMethodSet)
    ) {
      const tmpMethod = methodologySetList.find(
        item => item._id === existingConfig.selectedMethodSet._id
      )
      if (tmpMethod) {
        // Preserve per-run starccmConfig customizations over template defaults
        const methodSetToUse = cloneMethodologySet(tmpMethod)
        if (existingConfig.selectedMethodSet.starccmConfig) {
          methodSetToUse.starccmConfig = cloneStarCCMConfig(
            existingConfig.selectedMethodSet.starccmConfig
          )
        }
        setValue('selectedMethodSet', methodSetToUse)
      } else {
        setMethodologySetList([...methodologySetList, existingConfig.selectedMethodSet])
        setValue('selectedMethodSet', existingConfig.selectedMethodSet)
      }
    }

    setValue('baselineRunName', tmpRun.name)
    setValue('referenceRunName', tmpRun.name)

    const geoRunObj = geoRuns.find(item => item._id === tmpRun._id)
    const refRunObj = refRuns.find(item => item._id === tmpRun._id)
    if (!geoRunObj) {
      setGeoRuns([...geoRuns, tmpRun])
    }
    if (!refRunObj) {
      setRefRuns([...refRuns, tmpRun])
    }
    setValue('baselineRunId', dupeRun._id)
    setValue('referenceRunId', dupeRun._id)

    if (existingConfig?.selectedRHRepoSet) {
      const repoSet = rhRepoSetList.find(item => item._id === existingConfig.selectedRHRepoSet._id)
      if (repoSet) {
        setValue('selectedRHRepoSet', repoSet)
        setCurrentRHRepoSet(repoSet)
      }
    } else if (existingConfig.selectedAttitudeSet) {
      // Auto-populate RH Repo Set by matching attitude set name
      const matchedRHRepo = rhRepoSetList.find(
        rh => rh.name === existingConfig.selectedAttitudeSet.name
      )
      if (matchedRHRepo) {
        setValue('selectedRHRepoSet', matchedRHRepo)
        setCurrentRHRepoSet(matchedRHRepo)
      }
    }

    if (existingConfig?.selectedPtapLocationSet && existingConfig.selectedPtapLocationSet._id) {
      const locationSet = ptapLocationSetList.find(
        item => item._id === existingConfig.selectedPtapLocationSet._id
      )
      if (locationSet) {
        setValue('selectedPtapLocationSet', locationSet)
        setCurrentPtapLocationSet(locationSet)
      } else {
        setPtapLocationSetList([...ptapLocationSetList, existingConfig.selectedPtapLocationSet])
        setValue('selectedPtapLocationSet', existingConfig.selectedPtapLocationSet)
        setCurrentPtapLocationSet(existingConfig.selectedPtapLocationSet)
      }
    }

    setValue('description', existingConfig.description)

    // Compliance Team is deliberately not copied; each new run picks its own. Compliance
    // Description is only carried over to keep a drafting run drafting (MC projects lock it).
    if (isDraftingSource || project?.area === 'MC') {
      setValue('complianceDesc', 'Drafting')
    } else if (getValues('complianceDesc') === 'Drafting') {
      // Drop the drafting setup left by an earlier duplicate; its coordinates are cleared below.
      setValue('complianceDesc', '')
    }

    // Carry over the trailing car positions. They're stored in meters, so convert them back to the
    // car length/width multiples the form submits (the server scales them again on save).
    setCoordinatesInputMode('manual')
    setNewCoordinate({ x: undefined, y: undefined })
    setSelectedCoordinatesFile(null)
    setParsedCoordinatesRows([])
    setCoordinatesFromCSV([])
    const sourceFollowingCars = existingConfig.followingCars || []
    if (sourceFollowingCars.length > 0) {
      // The cached vehicle can be left over from another project. Only use its dimensions when it's
      // this project's vehicle, since that's the one the server scales the offsets by on save.
      const projectVehicle =
        currentVehicle && currentVehicle._id === project?.vehicleId ? currentVehicle : undefined
      const { coordinates: carUnitCoordinates, inCarUnits } = getFollowingCarCoordinates(
        sourceFollowingCars,
        projectVehicle
      )
      setCoordinates(inCarUnits ? carUnitCoordinates : [])
      if (!inCarUnits) {
        toast.error(
          'Vehicle dimensions are unavailable, so the trailing car coordinates could not be copied. Please re-enter them.'
        )
      }
    } else {
      setCoordinates([])
    }

    // Don't carry the override sim file into the new run, but keep the duplicated run's
    // template selectable in the Override Template Sim File dropdown. The template may live
    // at the run level (overrideSelectedSimFile) or on the cases (simTemplate).
    const inheritedCaseTemplate = (existingConfig.selectedCases || [])
      .map(c => c.simTemplate)
      .find(t => t?.file)
    if (existingConfig.overrideSelectedSimFile) {
      const srcSimOverrideDir =
        existingConfig.simOverrideDir || `${simOverrideRoot}/${gmid}/${tmpRun.name}`
      setDuplicatedSimTemplate({
        file: existingConfig.overrideSelectedSimFile,
        // Directory only (dir/file convention) — may be container-relative; the server resolves it.
        path: `${srcSimOverrideDir.replace(/\/$/, '')}/`,
        system: 'starccm',
        fileType: 'template',
        rootType: 'override'
      } as CFDFile)
    } else if (inheritedCaseTemplate?.file) {
      setDuplicatedSimTemplate({ ...inheritedCaseTemplate } as CFDFile)
    } else {
      setDuplicatedSimTemplate(null)
    }
    // New run starts with no override applied (methodology default).
    setValue('overrideSelectedSimFile', '')
    setSelectedSimFile([])

    // Each run should have its own geometry change image, so don't carry it over
    // from the duplicated run.
    setSelectedGeometryChangeImage([])
    setValue('geometryChangeImage', [])
  }

  const onValidationError: SubmitErrorHandler<RunConfig> = errors => {
    console.error('Validation errors:', errors)
    toast.error('Please fix the validation errors before submitting the form.')
    setIsSubmittingBackground(false)
  }

  const onSubmit: SubmitHandler<RunConfig> = async (formData: RunConfig) => {
    console.log('=== FORM SUBMISSION START ===')
    console.log('Run Form Data=', formData)
    console.log('StarCCM Config from ref:', refStarCCMConfig.current)
    try {
      // prepare RunConfig
      const tmpRunConfig = new RunConfigProto()

      tmpRunConfig.coresCount = formData.coresCount ?? 0
      tmpRunConfig.walltimeOverride = formData.walltimeOverride ?? 0
      tmpRunConfig.raceTeamId = currentTeam
      if (isF1Team) {
        tmpRunConfig.RATGstatus = formData.RATGstatus
      }
      tmpRunConfig.expandForVehicleStates = true
      tmpRunConfig.description = formData.description
      if (formData.selectedMethodSet) {
        tmpRunConfig.selectedMethodSet = cloneMethodologySet(formData.selectedMethodSet)

        // Migrate legacy hardware overrides: if the methodology has top-level hardware
        // overrides but per-system fields are empty, push them down to selectedSystems.
        if (
          tmpRunConfig.selectedMethodSet.isHardwareOverride &&
          tmpRunConfig.selectedMethodSet.hardwareOverrides &&
          tmpRunConfig.selectedMethodSet.selectedSystems
        ) {
          const anySystemHasHardware = tmpRunConfig.selectedMethodSet.selectedSystems.some(
            sys => sys.isHardwareOverride || sys.hardwareOverrides
          )
          if (!anySystemHasHardware) {
            tmpRunConfig.selectedMethodSet.selectedSystems.forEach(sys => {
              if (sys.name === 'starccmp' || sys.name === 'ensight') {
                sys.isHardwareOverride = true
                sys.hardwareOverrides = tmpRunConfig.selectedMethodSet.hardwareOverrides
              }
            })
          }
        }

        // Save StarCCM configuration if it exists
        if (refStarCCMConfig.current) {
          console.log('Cloning StarCCM config:', refStarCCMConfig.current)
          tmpRunConfig.selectedMethodSet.starccmConfig = cloneStarCCMConfig(
            refStarCCMConfig.current
          )
          console.log(
            'StarCCM config cloned successfully:',
            tmpRunConfig.selectedMethodSet.starccmConfig
          )

          // Filter systemCodeFiles to only include custom .java files that
          // the user actually selected in the StarCCM configuration.
          // The downstream HPC orchestrator builds starMacroSteps from
          // systemCodeFiles, so unselected custom steps must be removed.
          const selectedCustomSteps = new Set(refStarCCMConfig.current.customSteps || [])
          if (tmpRunConfig.selectedMethodSet.systemCodeFiles) {
            tmpRunConfig.selectedMethodSet.systemCodeFiles =
              tmpRunConfig.selectedMethodSet.systemCodeFiles.filter(entry => {
                // Keep non-code files (templates) unconditionally
                const fileType = entry.fileRef?.fileType || 'code'
                if (fileType !== 'code') return true
                // Keep code files only if the user selected them
                return selectedCustomSteps.has(entry.fileRef?.file)
              })
          }
        } else {
          console.log('No StarCCM config in ref to save')
        }
      }
      if (!isF1Team) {
        // When saving geo in background, include all non-failed files (incl. still-uploading ones)
        // so the DB has the full file list before the blobs finish landing.
        const geoFiles = saveGeoInBackgroundRef.current
          ? selectedGeoFiles.filter(file => !file.failed)
          : selectedGeoFiles.filter(file => !file.uploading && !file.failed)
        const geoStagingFiles = geoFiles.map(file => file.name)
        if (geoStagingFiles.length > 0) {
          formData.geoStagingFiles = geoStagingFiles
        } else {
          formData.geoStagingFiles = []
        }
        tmpRunConfig.geoStagingDir = formData.geoStagingDir
        tmpRunConfig.geoStagingFiles = formData.geoStagingFiles

        const geometryChangeImageFiles = saveGeoInBackgroundRef.current
          ? selectedGeometryChangeImage.filter(file => !file.failed)
          : selectedGeometryChangeImage.filter(file => !file.uploading && !file.failed)
        formData.geometryChangeImage = geometryChangeImageFiles.map(file => file.name)
        tmpRunConfig.geometryChangeImage = formData.geometryChangeImage
      }
      if (isF1Team && formData.selectedTrajectory) {
        tmpRunConfig.selectedTrajectory = cloneTrajectory(formData.selectedTrajectory)
      }
      if (formData.selectedAttitudeSet) {
        tmpRunConfig.selectedAttitudeSet = cloneAttitudeSet(formData.selectedAttitudeSet)
      }
      if (isF1Team && formData.selectedDomainSet) {
        tmpRunConfig.selectedDomainSet = cloneDomainSet(formData.selectedDomainSet)
      }
      if (isF1Team && formData.selectedTyreSet) {
        tmpRunConfig.selectedTyreSet = cloneTyreSet(formData.selectedTyreSet)
      }
      if (isF1Team && formData.selectedSimulationSet) {
        tmpRunConfig.selectedSimulationSet = cloneSimulationSet(formData.selectedSimulationSet)
      }
      if (formData.selectedPtapLocationSet) {
        tmpRunConfig.selectedPtapLocationSet = clonePtapLocationSet(
          formData.selectedPtapLocationSet
        )
      }
      if (formData.selectedRHRepoSet) {
        tmpRunConfig.selectedRHRepoSet = cloneRHRepoSet(formData.selectedRHRepoSet)
      }
      // Get cases from the inline case creator if available, otherwise fall back to form data.
      // Block the save while any per-case .sim upload is still uploading/verifying or failed to
      // verify, so the run isn't persisted with the previous/default SIM (the inline Create-New-Run
      // path goes through getProcessedCases, bypassing CaseCreator's own onSubmit guard).
      if (caseCreatorRef.current?.hasPendingSimUploads?.()) {
        toast.error(
          'A per-case SIM file is still uploading or failed to verify. Wait for it to be confirmed in Azure (or re-upload) before saving.'
        )
        setIsSubmittingBackground(false)
        return
      }
      const inlineCases = caseCreatorRef.current?.getProcessedCases()
      if (inlineCases && inlineCases.length > 0) {
        tmpRunConfig.selectedCases = inlineCases.map(item => {
          return cloneRunConfigCase(item)
        })
      } else if (formData.selectedCases) {
        tmpRunConfig.selectedCases = formData.selectedCases.map(item => {
          return cloneRunConfigCase(item)
        })
      } else if (mode === 'edit' && existingRun?.runConfig?.selectedCases) {
        tmpRunConfig.selectedCases = existingRun.runConfig.selectedCases.map(item => {
          return cloneRunConfigCase(item)
        })
      }
      if (formData.baselineRunId) {
        tmpRunConfig.baselineRunId = formData.baselineRunId as string
        const runName = geoRuns.find(item => item._id === formData.baselineRunId)?.name as string
        tmpRunConfig.baselineRunName = runName
      }
      if (formData.referenceRunId) {
        tmpRunConfig.referenceRunId = formData.referenceRunId as string
        const runName = refRuns.find(item => item._id === formData.referenceRunId)?.name as string
        tmpRunConfig.referenceRunName = runName
      }
      if (formData.template?._id) {
        tmpRunConfig.workflowSubmissionTemplate = cloneWorkflowSubmissionTemplate(formData.template)
      }
      if (formData.complianceTeam) {
        tmpRunConfig.complianceTeam = formData.complianceTeam
      }
      if (formData.complianceDesc) {
        tmpRunConfig.complianceDesc = formData.complianceDesc
      }
      if (formData.overrideSelectedSimFile) {
        tmpRunConfig.overrideSelectedSimFile = formData.overrideSelectedSimFile
        tmpRunConfig.simOverrideDir = formData.simOverrideDir
        // A run-level override wins over methodology defaults; stamp it onto every case except
        // deliberate per-case uploads (/sim-templates/). Mutate each proto case in place with a
        // CFDFile instance (never reassign the proto selectedCases array with plain objects, which
        // breaks the repeated-wrapper setter). Server forceFromDir rebuilds the path from simOverrideDir.
        ;(tmpRunConfig.selectedCases || []).forEach(caseItem => {
          if (isPerCaseUpload(caseItem.simTemplate)) return
          const overrideTemplate = new CFDFile()
          overrideTemplate.system = 'starccm'
          overrideTemplate.file = formData.overrideSelectedSimFile as string
          overrideTemplate.path = formData.simOverrideDir || ''
          overrideTemplate.fileType = 'template'
          overrideTemplate.rootType = 'override'
          caseItem.simTemplate = overrideTemplate
        })
      }

      // trailing car config
      if (mode === 'new' && formData.complianceDesc === 'Drafting') {
        let coords = []
        if (coordinatesInputMode === 'manual') {
          coords = [...coordinates]
          if (newCoordinate.x && newCoordinate.y) {
            coords.push(newCoordinate)
          }
          if (coords.length === 0) {
            toast.error('Please provide coordinates for the trailing car.')
            setIsSubmittingBackground(false)
            return
          }
        } else if (coordinatesInputMode === 'csv') {
          if (coordinatesFromCSV.length > 0) {
            coords = [...coordinatesFromCSV]
          } else {
            toast.error('Please upload a CSV file with coordinates for the trailing car.')
            setIsSubmittingBackground(false)
            return
          }
        }
        tmpRunConfig.followingCars = coords.map(coord => {
          const display = new FollowingCar()
          display.localPosition = new Coordinate()
          display.localPosition.distance_x_m = coord.x
          display.localPosition.distance_y_m = coord.y
          display.localPosition.distance_z_m = 0
          return display
        })
      }

      // prepare sumbission
      const tmpSubmission = new WorkflowSubmission()
      if (tmpSubmission) {
        // project info
        if (formData.project) {
          tmpSubmission.projectId = formData.project._id
          tmpSubmission.projectName = formData.project.name
        }
        tmpSubmission.raceTeamId = currentTeam

        if (formData.template?._id) {
          tmpSubmission.templateOriginId = formData.template._id
          tmpSubmission.templateOriginName = formData.template.name
        }

        console.log('=== FINAL RUN CONFIG ===')
        console.log('tmpRunConfig:', tmpRunConfig)
        console.log('tmpRunConfig.selectedMethodSet:', tmpRunConfig.selectedMethodSet)
        console.log(
          'tmpRunConfig.selectedMethodSet.starccmConfig:',
          tmpRunConfig.selectedMethodSet?.starccmConfig
        )
        console.log('tmpSubmission:', tmpSubmission)
        console.log('=== FORM SUBMISSION END ===')

        onConfirm &&
          onConfirm(
            tmpRunConfig,
            tmpSubmission,
            aeroTempRunId,
            gmid,
            saveSimInBackgroundRef.current ? (simUploadPromiseRef.current ?? undefined) : undefined,
            saveSimInBackgroundRef.current
              ? (cb: (pct: number) => void) => {
                  simProgressHook.current.onProgress = cb
                }
              : undefined,
            saveGeoInBackgroundRef.current ? getGeoUploadPromise() : undefined,
            saveGeoInBackgroundRef.current
              ? (cb: (pct: number) => void) => {
                  geoProgressHook.current.onProgress = cb
                }
              : undefined,
            sequenceNumberOverride,
            overrideSimSourceUrlRef.current
          )
        saveSimInBackgroundRef.current = false
        saveGeoInBackgroundRef.current = false
        setIsSubmittingBackground(false)
        // Dismiss the loading toast
        toast.dismiss('upload-batch')
      }
    } catch (err) {
      console.error(err)
      toast.error('Error saving run.')
      setIsSubmittingBackground(false)
    }
  }

  const handleConfirm = () => {
    onCancel && onCancel()
  }

  const handleClose = () => {
    const simUploading = selectedSimFile.some(f => f.uploading)
    const geoUploading =
      selectedGeoFiles.some(f => f.uploading) || selectedGeometryChangeImage.some(f => f.uploading)
    if (simUploading || geoUploading) {
      setShowCloseConfirmModal(true)
      return
    }
    onCancel && onCancel()
  }

  const handleConfirmClose = () => {
    // Abort any in-progress uploads
    selectedSimFile.forEach(f => f.abortController?.abort())
    selectedGeoFiles.forEach(f => f.abortController?.abort())
    selectedGeometryChangeImage.forEach(f => f.abortController?.abort())
    setShowCloseConfirmModal(false)
    onCancel && onCancel()
  }

  return (
    <Modal
      isOpen={true}
      withAction={false}
      easyClose={true}
      size="xl"
      onCancel={handleClose}
      title={editorTitle[mode] + (mode === 'edit' ? `: ${existingRun?.name}` : '')}
      showCancelButton={true}
      zIndex={30}
      onConfirm={handleConfirm}
      footer={
        <div className="flex items-center justify-end space-x-4">
          <div className="flex flex-col items-end gap-1">
            {isSubmittingBackground && (
              <span className="text-xs text-gray-400 dark:text-gray-500">
                This may take a few seconds…
              </span>
            )}
            <Button
              onClick={() => {
                const geoUploading =
                  selectedGeoFiles.some(file => file.uploading) ||
                  selectedGeometryChangeImage.some(file => file.uploading)
                if (geoUploading) {
                  setShowGeoBackgroundModal(true)
                  return
                }
                if (selectedSimFile.some(f => f.uploading)) {
                  setShowSimBackgroundModal(true)
                  return
                }
                handleSubmit(onSubmit, onValidationError)()
              }}
              disabled={submitting || isSubmittingBackground}>
              {(submitting || isSubmittingBackground) && (
                <LoadingIcon className="fill-white w-4 h-4 animate-spin" />
              )}
              {!submitting && !isSubmittingBackground && (
                <SaveIcon className="fill-white w-4 h-4" />
              )}
              <span className="ml-1">{isSubmittingBackground ? 'Saving…' : 'Save'}</span>
            </Button>
          </div>
          <CloseButton label="Close" onClick={handleClose} />
        </div>
      }>
      <div className="flex flex-col items-stretch">
        {mode === 'new' && (
          <div className="flex mt-4">
            <div className="flex items-center space-x-2 p-2 rounded-md bg-light-blue dark:bg-neutral-80 min-w-fit">
              <div className="dark:text-neutral">Duplicate Run</div>
              <Dropdown
                disabled={submitting}
                loading={loadingRun && runFilterFor === 'dup'}
                value={dupeRun}
                pt={{
                  ...DropdownStyle,
                  root: {
                    ...DropdownStyle.root,
                    className:
                      'min-w-[250px] max-w-[250px] dark:bg-transparent dark:border-neutral-70'
                  }
                }}
                filter
                filterBy="name"
                dataKey="_id"
                showClear
                onFilter={e => {
                  if (e.filter.length < 2) {
                    return
                  }
                  setRunFilterFor('dup')
                  const nameSearch = { $regex: e.filter, $options: 'i' }
                  const query = queryString2JsonString(
                    'name=' + encodeURIComponent(JSON.stringify(nameSearch))
                  )
                  fetchRuns(`query=${query}&proj=${simpleRunProjection}`)
                }}
                onChange={async e => {
                  if (e.value) {
                    e.value._id && setDupeRun(e.value)
                  } else {
                    setDupeRun(e.value)
                  }
                }}
                itemTemplate={item => {
                  return item._id ? item.name : null
                }}
                options={dupeRuns}
                optionLabel="name"
                optionValue="_id"
                useOptionAsValue
                placeholder="Select Run"
                emptyMessage="&nbsp;&nbsp;Use Search to select run"
                emptyFilterMessage="&nbsp;&nbsp;No runs found"
              />
              <Button
                onClick={handleDuplicateRun}
                disabled={!dupeRun || !dupeRun?._id || submitting}>
                {loadingOneRun && <LoadingIcon className="w-4 h-4 fill-white animate-spin" />}
                {!loadingOneRun && <CopyIcon className="w-4 h-4 fill-white" />}
                <span className="ml-1">Duplicate</span>
              </Button>
            </div>
          </div>
        )}
        <form
          onSubmit={e => {
            e.preventDefault()
            const geoUploading =
              selectedGeoFiles.some(file => file.uploading) ||
              selectedGeometryChangeImage.some(file => file.uploading)
            if (geoUploading) {
              setShowGeoBackgroundModal(true)
              return
            }
            if (selectedSimFile.some(f => f.uploading)) {
              setShowSimBackgroundModal(true)
              return
            }
            handleSubmit(onSubmit, onValidationError)()
          }}
          className="w-full grid grid-cols-3 gap-x-2 gap-y-4 mt-8">
          <div className="field-box">
            <label className="mb-2">Run Number Override</label>
            <InputNumber
              value={sequenceNumberOverride}
              onValueChange={e => setSequenceNumberOverride(e.value ?? null)}
              placeholder={`Auto → R${((project.runSequence ?? 0) + 1)
                .toString()
                .padStart(3, '0')}`}
              min={1}
              useGrouping={false}
              disabled={submitting}
              className="w-full"
              inputClassName="w-full"
            />
            <small className="text-gray-400 mt-1">
              Next auto: R{((project.runSequence ?? 0) + 1).toString().padStart(3, '0')}
            </small>
          </div>

          <div className="field-box">
            <Controller
              name="description"
              control={control}
              defaultValue=""
              disabled={submitting}
              rules={{ required: 'Description is required.' }}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className="mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <InputText
                    id={field.name}
                    {...field}
                    className={classNames({ error: fieldState.error })}
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>

          <div className="field-box col-span-1 !hidden">
            <Controller
              name="RATGstatus"
              control={control}
              defaultValue="True"
              rules={{ required: isF1Team ? 'RATG status is required.' : false }}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className={labelRequiredByF1Team}>
                    RATG {isF1Team && <span className="text-red-500">*</span>}
                  </label>
                  <Dropdown
                    value={field.value}
                    pt={DropdownStyle}
                    appendTo="self"
                    disabled={
                      !isF1Team ||
                      submitting ||
                      (mode === 'edit' && existingRun?.overallStatus !== RunStatus.CREATED)
                    }
                    onChange={e => {
                      field.onChange(e.value)
                    }}
                    className={classNames({ error: fieldState.error })}
                    options={['True', 'False']}
                    useOptionAsValue
                    placeholder="Select Status"
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>

          <div className="field-box  col-span-1">
            <Controller
              name="selectedMethodSet"
              disabled={
                submitting || (mode === 'edit' && existingRun?.overallStatus !== RunStatus.CREATED)
              }
              control={control}
              rules={{ required: 'Methodology set is required.' }}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className="mb-2">
                    Methodology Set <span className="text-red-500">*</span>
                  </label>
                  <Dropdown
                    value={field.value}
                    pt={DropdownStyle}
                    appendTo="self"
                    onChange={e => {
                      field.onChange(e.value)
                    }}
                    className={classNames({ error: fieldState.error })}
                    options={visibleMethodologySetList}
                    filter
                    itemTemplate={item => {
                      return (
                        <div className="flex flex-col items-stretch" title={item.description}>
                          <div className="font-bold">{item.name}</div>
                          {item.description && (
                            <div className="text-sm text-gray-400 max-w-[300px] break-words whitespace-normal">
                              {item.description}
                            </div>
                          )}
                        </div>
                      )
                    }}
                    valueTemplate={item => {
                      return item ? (
                        <div className="flex items-center" title={item.description}>
                          <span>{item.name}</span>
                          {item.description && (
                            <div className="ml-2 text-sm text-gray-400 max-w-[300px] overflow-hidden text-ellipsis">{`(${item.description})`}</div>
                          )}
                        </div>
                      ) : (
                        'Select Methodology Set'
                      )
                    }}
                    filterBy="name"
                    optionLabel="name"
                    dataKey="_id"
                    useOptionAsValue
                    placeholder="Select Methodology Set"
                    emptyMessage="&nbsp;&nbsp;No data"
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>

          <div className="field-box">
            <Controller
              name="template"
              disabled={
                submitting || (mode === 'edit' && existingRun?.overallStatus !== RunStatus.CREATED)
              }
              control={control}
              rules={{ required: 'Workflow template is required.' }}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className="mb-2">
                    Workflow Template <span className="text-red-500">*</span>
                  </label>
                  <Dropdown
                    value={field.value}
                    pt={DropdownStyle}
                    appendTo="self"
                    onChange={e => {
                      console.log(e)
                      field.onChange(e.value)
                    }}
                    filter
                    filterBy="name"
                    className={classNames({ error: fieldState.error })}
                    options={filteredTemplateList}
                    optionLabel="name"
                    optionValue="_id"
                    useOptionAsValue
                    placeholder="Select Template"
                    emptyMessage="&nbsp;&nbsp;No data"
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>

          <div className="field-box">
            <Controller
              name="selectedAttitudeSet"
              disabled={
                submitting || (mode === 'edit' && existingRun?.overallStatus !== RunStatus.CREATED)
              }
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className="mb-2">
                    Attitude Set
                  </label>
                  <Dropdown
                    value={field.value}
                    pt={DropdownStyle}
                    appendTo="self"
                    filter
                    onChange={e => {
                      field.onChange(e.value)
                      setCurrentAttitudeSet(e.value as AttitudeSet)
                      // @ts-ignore
                      setTrajectoryList((e.value as AttitudeSet).trajectories || [])
                      // @ts-ignore
                      setValue('selectedTrajectory', null as any)
                      // Auto-populate RH Repo Set by matching name
                      const matchedRHRepo = rhRepoSetList.find(
                        rh => rh.name === (e.value as AttitudeSet).name
                      )
                      setValue('selectedRHRepoSet', matchedRHRepo || (null as any))
                      setCurrentRHRepoSet(matchedRHRepo || null)
                      // Duplicated cases belong to the previous set; CaseCreator re-maps its own copy
                      setValue('selectedCases', undefined as any)
                    }}
                    className={classNames({ error: fieldState.error })}
                    options={attitudeSetList}
                    optionLabel="name"
                    optionValue="_id"
                    useOptionAsValue
                    placeholder="Select Attitude Set"
                    emptyMessage="&nbsp;&nbsp;No data"
                    itemTemplate={item => {
                      return item.name + (item.status ? ` (${item.status.substring(0, 1)})` : '')
                    }}
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>

          <div className="field-box !hidden">
            <Controller
              name="selectedTrajectory"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className={labelRequiredByF1Team}>
                    Trajectory
                  </label>
                  <Dropdown
                    value={field.value}
                    pt={DropdownStyle}
                    appendTo="self"
                    disabled={
                      !isF1Team ||
                      submitting ||
                      (mode === 'edit' && existingRun?.overallStatus !== RunStatus.CREATED)
                    }
                    onChange={e => {
                      field.onChange(e.value)
                    }}
                    className={classNames({ error: fieldState.error })}
                    options={trajectoryList}
                    optionLabel="name"
                    optionValue="name"
                    useOptionAsValue
                    placeholder="Select Trajectory"
                    emptyMessage="&nbsp;&nbsp;No Trajectories"
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>

          <div className="field-box !hidden">
            <Controller
              name="selectedDomainSet"
              control={control}
              rules={{ required: isF1Team ? 'Domain set is required.' : false }}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className={labelRequiredByF1Team}>
                    Domain Set {isF1Team && <span className="text-red-500">*</span>}
                  </label>
                  <Dropdown
                    disabled={
                      !isF1Team ||
                      submitting ||
                      (mode === 'edit' && existingRun?.overallStatus !== RunStatus.CREATED)
                    }
                    value={field.value}
                    pt={DropdownStyle}
                    appendTo="self"
                    filter
                    onChange={e => field.onChange(e.value)}
                    className={classNames({ error: fieldState.error })}
                    options={domainSetList}
                    optionLabel="name"
                    optionValue="_id"
                    useOptionAsValue
                    readOnly={mode === 'view'}
                    placeholder="Select Domain Set"
                    emptyMessage="&nbsp;&nbsp;No Domain Set"
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>
          <div className="field-box !hidden">
            <Controller
              name="selectedTyreSet"
              control={control}
              rules={{ required: isF1Team ? 'Tire set is required.' : false }}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className={labelRequiredByF1Team}>
                    Tire Set {isF1Team && <span className="text-red-500">*</span>}
                  </label>
                  <Dropdown
                    disabled={
                      !isF1Team ||
                      submitting ||
                      (mode === 'edit' && existingRun?.overallStatus !== RunStatus.CREATED)
                    }
                    value={field.value}
                    pt={DropdownStyle}
                    appendTo="self"
                    filter
                    onChange={e => field.onChange(e.value)}
                    className={classNames({ error: fieldState.error })}
                    options={tyreSetList}
                    optionLabel="name"
                    optionValue="_id"
                    useOptionAsValue
                    readOnly={mode === 'view'}
                    placeholder="Select Tire Set"
                    emptyMessage="&nbsp;&nbsp;No Tire Set"
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>

          <div className="field-box !hidden">
            <Controller
              name="selectedSimulationSet"
              control={control}
              rules={{ required: isF1Team ? 'Simulation set is required.' : false }}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className={labelRequiredByF1Team}>
                    Simulation Set {isF1Team && <span className="text-red-500">*</span>}
                  </label>
                  <Dropdown
                    disabled={
                      !isF1Team ||
                      submitting ||
                      (mode === 'edit' && existingRun?.overallStatus !== RunStatus.CREATED)
                    }
                    value={field.value}
                    pt={DropdownStyle}
                    appendTo="self"
                    filter
                    onChange={e => field.onChange(e.value)}
                    className={classNames({ error: fieldState.error })}
                    options={simulationSetList}
                    optionLabel="name"
                    optionValue="_id"
                    useOptionAsValue
                    readOnly={mode === 'view'}
                    placeholder="Select Simulation Set"
                    emptyMessage="&nbsp;&nbsp;No Simulation Set"
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>

          <div className="field-box">
            <Controller
              name="baselineRunId"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className="mb-2">
                    Master Geometry
                  </label>
                  <Dropdown
                    disabled={
                      submitting ||
                      (mode === 'edit' && existingRun?.overallStatus !== RunStatus.CREATED)
                    }
                    value={field.value}
                    pt={DropdownStyle}
                    appendTo="self"
                    filter
                    showClear
                    filterBy="name"
                    optionLabel="name"
                    optionValue="_id"
                    loading={loadingRun && runFilterFor === 'geo'}
                    onFilter={e => {
                      if (e.filter.length < 2) {
                        return
                      }
                      setRunFilterFor('geo')
                      const nameSearch = { $regex: e.filter, $options: 'i' }
                      const query = queryString2JsonString(
                        'name=' + encodeURIComponent(JSON.stringify(nameSearch))
                      )
                      fetchRuns(`query=${query}&proj=${simpleRunProjection}`)
                    }}
                    onChange={e => {
                      if (e.value) {
                        field.onChange(e.value)
                        // Fetch master geometry run's sim templates
                        const selectedGeoRun = geoRuns.find(r => r._id === e.value)
                        if (selectedGeoRun) {
                          fetchOneRun(selectedGeoRun._id)
                            .then(resp => {
                              if (resp?.data?.protoBytesList?.length) {
                                const runs = protoService.decodeProtoResponse(
                                  resp.data,
                                  'workflowsubmission'
                                )
                                const geoRunConfig = runs[0]?.runConfig
                                const templates: any[] = []
                                if (geoRunConfig?.overrideSelectedSimFile) {
                                  templates.push({
                                    system: 'starccm',
                                    file: geoRunConfig.overrideSelectedSimFile,
                                    fileType: 'template',
                                    rootType: 'master-geometry'
                                  })
                                }
                                if (geoRunConfig?.selectedMethodSet?.systemCodeFiles) {
                                  geoRunConfig.selectedMethodSet.systemCodeFiles
                                    .filter(
                                      (f: any) =>
                                        f.fileRef?.fileType === 'template' &&
                                        f.systemType?.toLowerCase() === 'starccm'
                                    )
                                    .forEach((f: any) => {
                                      templates.push({
                                        system: 'starccm',
                                        file: f.fileName || f.fileRef?.file || '',
                                        fileType: 'template',
                                        rootType: 'master-geometry'
                                      })
                                    })
                                }
                                setMasterGeoTemplates(templates)
                              }
                            })
                            .catch(() => {})
                        }
                      } else {
                        // @ts-ignore
                        setValue('baselineRunId', '')
                        setMasterGeoTemplates([])
                      }
                    }}
                    className={classNames({ error: fieldState.error })}
                    options={geoRuns}
                    placeholder="None"
                    emptyMessage="&nbsp;&nbsp;Use Search to select run"
                    emptyFilterMessage="&nbsp;&nbsp;No runs found"
                    filterPlaceholder="Enter run ID to search"
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>

          <div className="field-box">
            <Controller
              name="referenceRunId"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className="mb-2">
                    Reference Run
                  </label>
                  <Dropdown
                    disabled={
                      submitting ||
                      (mode === 'edit' && existingRun?.overallStatus !== RunStatus.CREATED)
                    }
                    loading={loadingRun && runFilterFor === 'ref'}
                    value={field.value}
                    pt={DropdownStyle}
                    appendTo="self"
                    filter
                    filterBy="name"
                    dataKey="_id"
                    showClear
                    onFilter={e => {
                      if (e.filter.length < 2) {
                        return
                      }
                      setRunFilterFor('ref')
                      const nameSearch = { $regex: e.filter, $options: 'i' }
                      const query = queryString2JsonString(
                        'name=' + encodeURIComponent(JSON.stringify(nameSearch))
                      )
                      fetchRuns(`query=${query}&proj=${simpleRunProjection}`)
                    }}
                    onChange={e => {
                      if (e.value) {
                        field.onChange(e.value)
                      } else {
                        setValue('referenceRunId', '')
                      }
                    }}
                    className={classNames({ error: fieldState.error })}
                    options={refRuns}
                    optionLabel="name"
                    optionValue="_id"
                    placeholder="None"
                    emptyMessage="&nbsp;&nbsp;Use Search to select run"
                    emptyFilterMessage="&nbsp;&nbsp;No runs found"
                    filterPlaceholder="Enter run ID to search"
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>

          <div className="field-box">
            <Controller
              name="selectedPtapLocationSet"
              control={control}
              rules={{ required: false }}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className="mb-2">
                    Pressure Tap Files
                  </label>
                  <Dropdown
                    disabled={
                      submitting ||
                      (mode === 'edit' && existingRun?.overallStatus !== RunStatus.CREATED)
                    }
                    value={field.value}
                    pt={DropdownStyle}
                    appendTo="self"
                    filter
                    onChange={e => {
                      field.onChange(e.value)
                      setCurrentPtapLocationSet(e.value as PtapLocationSet)
                    }}
                    className={classNames({ error: fieldState.error })}
                    options={ptapLocationSetList}
                    optionLabel="version"
                    optionValue="_id"
                    valueTemplate={item => {
                      return item ? item.version : 'Select Pressure Tap Files'
                    }}
                    itemTemplate={item => {
                      return item.version
                    }}
                    useOptionAsValue
                    readOnly={mode === 'view'}
                    loading={loadingPtap}
                    placeholder="Select Pressure Tap Files"
                    emptyMessage="&nbsp;&nbsp;No Files"
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>

          <div className="field-box">
            <Controller
              name="selectedRHRepoSet"
              control={control}
              rules={{ required: isF1Team ? 'Ride Height Files is required.' : false }}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className="mb-2">
                    Ride Height Files {isF1Team && <span className="text-red-500">*</span>}
                  </label>
                  <Dropdown
                    disabled={
                      submitting ||
                      (mode === 'edit' && existingRun?.overallStatus !== RunStatus.CREATED)
                    }
                    value={field.value}
                    pt={DropdownStyle}
                    appendTo="self"
                    filter
                    onChange={e => field.onChange(e.value)}
                    className={classNames({ error: fieldState.error })}
                    options={rhRepoSetList}
                    optionLabel="name"
                    optionValue="_id"
                    useOptionAsValue
                    readOnly={mode === 'view'}
                    loading={loadingRHRepo}
                    valueTemplate={item => {
                      return item ? item.name + '/' + item.version : 'Select Right Height Files'
                    }}
                    itemTemplate={item => {
                      return item.name + '/' + item.version
                    }}
                    placeholder="Select Right Height Files"
                    emptyMessage="&nbsp;&nbsp;No Files"
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>

          <div className={classNames('field-box col-span-1', { '!hidden': isF1Team })}>
            <label className="mb-2">Sim Template</label>
            <SimTemplateDropdown
              submissionId={existingRun?._id}
              submissionName={existingRun?.name || getValues('name') || ''}
              methodologyTemplates={methodologyTemplates}
              extraOptions={duplicatedSimTemplate ? [duplicatedSimTemplate] : undefined}
              value={overrideDropdownValue}
              onChange={handlePickOverrideTemplate}
              placeholder="Select sim template"
              autoSelectFirstOption={false}
              disabled={
                submitting || (mode === 'edit' && existingRun?.overallStatus !== RunStatus.CREATED)
              }
            />
            <div className="text-sm text-gray-500 mt-1">
              {overrideDropdownValue?.file
                ? `(${overrideDropdownValue.rootType}-${
                    overrideDropdownValue.fileType ||
                    overrideDropdownValue.rootType ||
                    overrideDropdownValue.system
                  }) ${overrideDropdownValue.file}`
                : 'None'}
            </div>
            <div className="mb-2" />
            <Controller
              name="overrideSelectedSimFile"
              control={control}
              rules={{ required: false }}
              render={({ field, fieldState }) => (
                <Upload
                  label="…or upload a new .sim"
                  field={field}
                  fieldState={fieldState}
                  setValue={setValue}
                  selectedFiles={selectedSimFile}
                  setSelectedFiles={setSelectedSimFile}
                  getFormErrorMessage={getFormErrorMessage}
                  uploadType={UploadTypes.OVERRIDE_STARCCM_TEMPLATE_FILE}
                  uploadQuantity={'single'}
                  setTempId={id => setAeroTempRunId(id)}
                  blobPrefix={`${gmid}${existingRun ? '/' + existingRun.name : ''}`}
                  isNew={mode === 'new'}
                  onUploadPromiseCreated={promise => {
                    simUploadPromiseRef.current = promise
                    // A fresh upload supersedes any dropdown-selected source.
                    overrideSimSourceUrlRef.current = null
                  }}
                  onProgressUpdate={pct => simProgressHook.current.onProgress(pct)}
                  disabled={
                    submitting ||
                    (mode === 'edit' && existingRun?.overallStatus !== RunStatus.CREATED)
                  }
                />
              )}
            />
          </div>

          <div className={classNames('field-box col-span-1', { '!hidden': isF1Team })}>
            <Controller
              name="geoStagingFiles"
              control={control}
              rules={{ required: false }}
              render={({ field, fieldState }) => (
                <Upload
                  label="Geometry Staging Files"
                  field={field}
                  fieldState={fieldState}
                  setValue={setValue}
                  selectedFiles={selectedGeoFiles}
                  setSelectedFiles={setSelectedGeoFiles}
                  getFormErrorMessage={getFormErrorMessage}
                  uploadType={UploadTypes.GEOMETRY_CHANGE_FILE}
                  setTempId={id => setAeroTempRunId(id)}
                  blobPrefix={`${gmid}${existingRun ? '/' + existingRun.name : ''}`}
                  isNew={mode === 'new'}
                  onUploadPromiseCreated={promise => {
                    registerGeoUploadPromise(promise)
                  }}
                  onProgressUpdate={pct => geoProgressHook.current.onProgress(pct)}
                  disabled={
                    submitting ||
                    (mode === 'edit' && existingRun?.overallStatus !== RunStatus.CREATED)
                  }
                />
              )}
            />
          </div>

          <div className={classNames('field-box col-span-1', { '!hidden': isF1Team })}>
            <Controller
              name="geometryChangeImage"
              control={control}
              rules={{
                validate: () =>
                  isF1Team ||
                  selectedGeometryChangeImage.some(file => !file.failed) ||
                  'Geometry Change Image is required.'
              }}
              render={({ field, fieldState }) => (
                <Upload
                  label={
                    <>
                      <span>Geometry Change Image</span>
                      <span className="pl-1 text-red-500">*</span>
                    </>
                  }
                  field={field}
                  fieldState={fieldState}
                  setValue={setValue}
                  selectedFiles={selectedGeometryChangeImage}
                  setSelectedFiles={setSelectedGeometryChangeImage}
                  getFormErrorMessage={getFormErrorMessage}
                  uploadType={UploadTypes.GEOMETRY_CHANGE_FILE}
                  accept="image/*"
                  setTempId={id => setAeroTempRunId(id)}
                  blobPrefix={`${gmid}${existingRun ? '/' + existingRun.name : ''}`}
                  isNew={mode === 'new'}
                  onUploadPromiseCreated={promise => {
                    registerGeoUploadPromise(promise)
                  }}
                  onProgressUpdate={pct => geoProgressHook.current.onProgress(pct)}
                  disabled={
                    submitting ||
                    (mode === 'edit' && existingRun?.overallStatus !== RunStatus.CREATED)
                  }
                />
              )}
            />
          </div>

          {
            /* Compliance Name and Description fields for NASCAR teams */
            NASCARTeams.includes(currentTeam) && (
              <>
                {(currentTeam === 'NASCAR-Cup' ||
                  currentTeam?.toLowerCase().startsWith('test')) && (
                  <div className="field-box">
                    <Controller
                      name="complianceTeam"
                      control={control}
                      disabled={submitting}
                      rules={{
                        required:
                          currentTeam === 'NASCAR-Cup' ? 'Compliance Name is required.' : false
                      }}
                      render={({ field, fieldState }) => (
                        <>
                          <label htmlFor={field.name} className="mb-2">
                            Compliance Team{' '}
                            {currentTeam === 'NASCAR-Cup' && (
                              <span className="text-red-500">*</span>
                            )}
                          </label>
                          <Dropdown
                            disabled={
                              submitting ||
                              (mode === 'edit' && existingRun?.overallStatus !== RunStatus.CREATED)
                            }
                            value={field.value}
                            pt={DropdownStyle}
                            appendTo="self"
                            showClear
                            onChange={e => {
                              if (e.value) {
                                field.onChange(e.value)
                              } else {
                                // @ts-ignore
                                setValue('complianceTeam', '')
                              }
                            }}
                            className={classNames({ error: fieldState.error })}
                            options={ComplianceTeamOptions}
                            placeholder="None"
                          />
                          {getFormErrorMessage(field.name)}
                        </>
                      )}
                    />
                  </div>
                )}
                <div className="field-box">
                  <Controller
                    name="complianceDesc"
                    control={control}
                    disabled={submitting}
                    rules={{
                      required: NASCARTeams.includes(currentTeam)
                        ? 'Compliance Description is required.'
                        : false
                    }}
                    render={({ field, fieldState }) => (
                      <>
                        <label htmlFor={field.name} className="mb-2">
                          Compliance Description{' '}
                          {NASCARTeams.includes(currentTeam) && (
                            <span className="text-red-500">*</span>
                          )}
                        </label>
                        <Dropdown
                          disabled={
                            submitting ||
                            project?.area === 'MC' ||
                            (mode === 'edit' && existingRun?.overallStatus !== RunStatus.CREATED)
                          }
                          value={field.value}
                          pt={DropdownStyle}
                          appendTo="self"
                          showClear
                          onChange={e => {
                            if (e.value) {
                              field.onChange(e.value)
                            } else {
                              // @ts-ignore
                              setValue('complianceDesc', '')
                            }
                          }}
                          className={classNames({ error: fieldState.error })}
                          options={ComplianceDescOptions}
                          placeholder="None"
                        />
                        {getFormErrorMessage(field.name)}
                      </>
                    )}
                  />
                </div>
                {complianceDesc === 'Drafting' && (
                  <div className="flex flex-col items-stretch border-none">
                    <div>Trailing Car Configuration</div>
                    <div className="flex flex-col items-stretch border border-solid border-[#e5e7eb] dark:border-neutral-80 rounded-lg mt-2 shadow-[0_1px_3px_0_rgba(0,0,0,0.10),0_1px_2px_-1px_rgba(0,0,0,0.10)]">
                      <div className="flex p-2 items-center justify-between border-b border-solid border-[#e5e7eb] rounded-t-lg bg-[#f9fafb] dark:border-neutral-80 dark:bg-neutral-80 dark:border-neutral-80 dark:text-neutral-20">
                        <div className="basis-0 grow">COORDINATES</div>
                        <div className="flex items-center justify-end basis-0 grow">
                          {coordinatesEditable && (
                            <SelectButton
                              value={coordinatesInputMode as string}
                              onChange={e => {
                                setCoordinatesInputMode(e.value)
                              }}
                              options={[
                                { label: 'Manual', value: 'manual' },
                                { label: 'CSV', value: 'csv' }
                              ]}
                              pt={{
                                root: {
                                  className: 'p-0 border-gray-300'
                                },
                                button: {
                                  className: 'text-xs border-gray-300 !px-1 !py-[2px]'
                                },
                                label: {
                                  className: 'text-xs border-gray-300 !px-1 !py-[2px]'
                                }
                              }}
                            />
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-stretch p-2 space-y-2">
                        {coordinatesInputMode === 'csv' && coordinatesEditable && (
                          <div className="pt-0">
                            {!selectedCoordinatesFile ? (
                              <FileUpload
                                mode="basic"
                                name="coordinates"
                                accept="text/csv"
                                className="mx-2"
                                onSelect={async e => {
                                  const file = e.files?.[0] || null
                                  setSelectedCoordinatesFile(file)

                                  if (!file) {
                                    setParsedCoordinatesRows([])
                                    setCoordinates([])
                                    return
                                  }

                                  try {
                                    const csvText = await file.text()
                                    const parsedRows = parseCoordinateCsv(csvText)
                                    setParsedCoordinatesRows(parsedRows)
                                    setCoordinatesFromCSV(
                                      parsedRows.map(row => ({ x: row.x, y: row.y }))
                                    )

                                    if (parsedRows.length === 0) {
                                      toast.error('No valid coordinate rows found in CSV.')
                                    }
                                  } catch (error) {
                                    setParsedCoordinatesRows([])
                                    setCoordinatesFromCSV([])
                                    toast.error('Failed to parse CSV file.')
                                  }
                                }}
                                chooseLabel="Choose a CSV file"
                                pt={{
                                  basicButton: {
                                    className:
                                      '!py-1 !px-4 cursor-pointer bg-gm-blue-dark border-gm-blue-dark text-white'
                                  },
                                  label: {
                                    className: 'text-sm'
                                  },
                                  chooseIcon: {
                                    className: 'w-3 h-3'
                                  }
                                }}
                              />
                            ) : (
                              <div className="flex items-center justify-between p-2 mt-1 mx-2 border border-solid border-[#e5e7eb] rounded-md">
                                <div className="flex items-center min-w-0 pr-2 space-x-2">
                                  <FileEditIcon className="w-4 h-4 stroke-[#99a1af]" />
                                  <span className="text-sm text-[#1f2937] dark:text-neutral-20 truncate">
                                    {selectedCoordinatesFile.name}
                                  </span>
                                  <span className="text-xs text-[#6b7280] dark:text-[#99a1af]">
                                    ({parsedCoordinatesRows.length} rows)
                                  </span>
                                </div>
                                <Button
                                  type="button"
                                  className="!px-2 !py-1 !bg-transparent border-none"
                                  onClick={() => {
                                    setSelectedCoordinatesFile(null)
                                    setParsedCoordinatesRows([])
                                    setCoordinatesFromCSV([])
                                  }}>
                                  <span className="pi pi-times text-warning"></span>
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="flex items-center justify-end space-x-2 px-2">
                          <div className="pt-2 pb-[2px] border-b-2 border-solid border-[#1E2939] text-[#4A5565] dark:border-neutral-20 dark:text-neutral-20 basis-0 grow">
                            Car Length(s)
                          </div>
                          <div className="pt-2 pb-[2px] border-b-2 border-solid border-[#1E2939] text-[#4A5565] dark:border-neutral-20 dark:text-neutral-20 basis-0 grow">
                            Car Width(s)
                          </div>
                          <div className="w-8"></div>
                        </div>
                        {coordinatesInputMode === 'manual' && coordinates.length > 0 && (
                          <ul className="max-h-[300px] overflow-y-auto">
                            {coordinates.map((coord, index) => (
                              <li
                                key={index}
                                className="flex items-center justify-end space-x-2 px-2 mb-2">
                                <InputNumber
                                  value={coord.x}
                                  readOnly={!coordinatesEditable}
                                  onValueChange={e => {
                                    if (!coordinatesEditable) return
                                    const newCoords = [...coordinates]
                                    newCoords[index] = { ...newCoords[index], x: e.value }
                                    setCoordinates(newCoords)
                                  }}
                                  placeholder="Enter X"
                                  className="basis-0 grow min-w-16 "
                                  inputClassName="!py-0.5 !px-2 !max-w-full"
                                />
                                <InputNumber
                                  value={coord.y}
                                  readOnly={!coordinatesEditable}
                                  onValueChange={e => {
                                    if (!coordinatesEditable) return
                                    const newCoords = [...coordinates]
                                    newCoords[index] = { ...newCoords[index], y: e.value }
                                    setCoordinates(newCoords)
                                  }}
                                  placeholder="Enter Y"
                                  inputClassName="!py-0.5 !px-2 !max-w-full"
                                  className="basis-0 grow min-w-16 placeholder:text-sm"
                                />
                                <Button
                                  type="button"
                                  disabled={!coordinatesEditable}
                                  className="!px-1 !py-0 w-8 !bg-transparent border-none"
                                  onClick={() => {
                                    if (!coordinatesEditable) return
                                    const newCoords = [...coordinates]
                                    newCoords.splice(index, 1)
                                    setCoordinates(newCoords)
                                  }}>
                                  <span className="pi pi-times text-[#99a1af]"></span>
                                </Button>
                              </li>
                            ))}
                          </ul>
                        )}
                        {coordinatesInputMode === 'manual' && coordinatesEditable && (
                          <div className="flex items-center space-x-2 px-2">
                            <InputNumber
                              inputRef={newCoordinateXInputRef}
                              value={newCoordinate.x ?? null}
                              onValueChange={e => {
                                setNewCoordinate({ ...newCoordinate, x: e.value ?? undefined })
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault()
                                  newCoordinateYInputRef.current?.focus()
                                }
                              }}
                              placeholder="Enter X"
                              inputClassName="!py-0.5 !px-2 !max-w-full"
                              className="basis-0 grow min-w-16 placeholder:text-sm"
                            />
                            <InputNumber
                              inputRef={newCoordinateYInputRef}
                              value={newCoordinate.y ?? null}
                              onValueChange={e => {
                                setNewCoordinate({ ...newCoordinate, y: e.value ?? undefined })
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault()
                                  addManualCoordinate(parseInputNumberValue(e.currentTarget.value))
                                }
                              }}
                              placeholder="Enter Y"
                              inputClassName="!py-0.5 !px-2 !max-w-full"
                              className="basis-0 grow min-w-16 "
                            />
                            <Button
                              type="button"
                              className="p-1 text-white w-8 !bg-transparent border-none"
                              onClick={() => {
                                addManualCoordinate()
                              }}>
                              <span className="pi pi-plus-circle text-gm-blue-dark dark:text-neutral-20"></span>
                            </Button>
                          </div>
                        )}
                        {coordinatesInputMode === 'csv' && coordinatesFromCSV.length > 0 && (
                          <ul>
                            {coordinatesFromCSV.map((coord, index) => (
                              <li
                                key={index}
                                className="flex items-center justify-end space-x-2 px-2 mb-2">
                                <InputNumber
                                  value={coord.x}
                                  readOnly={!coordinatesEditable}
                                  onValueChange={e => {
                                    const newCoords = [...coordinatesFromCSV]
                                    newCoords[index] = { ...newCoords[index], x: e.value }
                                    setCoordinatesFromCSV(newCoords)
                                  }}
                                  placeholder="Enter X"
                                  className="basis-0 grow min-w-16 "
                                  inputClassName="!py-0.5 !px-2 !max-w-full"
                                />
                                <InputNumber
                                  value={coord.y}
                                  readOnly={!coordinatesEditable}
                                  onValueChange={e => {
                                    const newCoords = [...coordinatesFromCSV]
                                    newCoords[index] = { ...newCoords[index], y: e.value }
                                    setCoordinatesFromCSV(newCoords)
                                  }}
                                  placeholder="Enter Y"
                                  inputClassName="!py-0.5 !px-2 !max-w-full"
                                  className="basis-0 grow min-w-16 placeholder:text-sm"
                                />
                                <div className="!px-1 !py-0 w-8 bg-transparent border-none"></div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {complianceDesc === 'Drafting' && (
                  <div>
                    <div className="col-span-1 flex space-x-2 items-stretch p-4 border border-solid border-[#dbeafe] rounded-lg bg-[rgba(239,246,255,0.5)] dark:bg-transparent dark:border-neutral-80 mt-7 shadow-[0_1px_3px_0_rgba(0,0,0,0.10),0_1px_2px_-1px_rgba(0,0,0,0.10)]">
                      <div className="pi pi-info-circle text-[#155dfc] dark:text-neutral-20"></div>
                      <div className="flex flex-col items-stretch">
                        <div className="text-[#1c398e] dark:text-neutral-20 font-semibold text-xs">
                          Drafting Mode Active
                        </div>
                        <div className="text-[rgba(25,60,184,0.80)] dark:text-neutral-20 mt-1 dark:text-[rgba(255,255,255,0.80)]">
                          Configure trailing car dimensions relative to the master geometry to
                          simulate aerodynamic slipstream effects.
                        </div>
                      </div>
                    </div>
                    <TrailingCarScatterPlot
                      coordinates={
                        coordinatesInputMode === 'csv' ? coordinatesFromCSV : coordinates
                      }
                    />
                  </div>
                )}
              </>
            )
          }
          {/* placeholder */}
          <div className="field-box"></div>

          {
            /* Create Cases Section */
            control._formValues?.selectedAttitudeSet && (
              <div className="col-span-3 mt-4">
                <div className="border border-solid border-[#dee2e6] rounded-md overflow-visible">
                  <button
                    type="button"
                    className="flex items-center w-full p-4 bg-[#f8f9fa] dark:bg-neutral-80 font-bold text-left cursor-pointer border-none"
                    onClick={() => setCasesExpanded(prev => !prev)}>
                    <span
                      className={`pi ${
                        casesExpanded ? 'pi-chevron-down' : 'pi-chevron-right'
                      } mr-2`}></span>
                    Add Case(s)
                  </button>
                  <div style={{ display: casesExpanded ? 'block' : 'none' }} className="p-4">
                    <CaseCreatorModal
                      ref={caseCreatorRef}
                      inline={true}
                      submitting={submitting}
                      initialCases={getValues('selectedCases') || []}
                      submission={
                        {
                          // A duplicated run is a new run: don't pass the source run's id, or
                          // CaseCreator would fetch and re-apply the source run's override template.
                          _id: existingRun?._id || '',
                          name: existingRun?.name || '',
                          runConfig: {
                            selectedAttitudeSet: currentAttitudeSet ||
                              getValues('selectedAttitudeSet') || {
                                attitudeStates: []
                              },
                            selectedDomainSet: getValues('selectedDomainSet'),
                            selectedTyreSet: getValues('selectedTyreSet'),
                            selectedSimulationSet: getValues('selectedSimulationSet'),
                            selectedPtapLocationSet:
                              currentPtapLocationSet || getValues('selectedPtapLocationSet'),
                            selectedRHRepoSet: currentRHRepoSet || getValues('selectedRHRepoSet'),
                            selectedMethodSet: getValues('selectedMethodSet'),
                            overrideSelectedSimFile: watchedOverrideSimFile,
                            simOverrideDir: watchedSimOverrideDir,
                            masterGeoTemplates: masterGeoTemplates
                          }
                        } as unknown as WorkflowSubmission
                      }
                      onCancel={() => {}}
                      onConfirm={() => {}}
                    />
                  </div>
                </div>
              </div>
            )
          }
          {
            /* StarCCM Configuration Section */
            control._formValues?.selectedMethodSet && (
              <div className="col-span-3 mt-8">
                <Accordion>
                  <AccordionTab header="StarCCM+ Configuration">
                    <StarCCMRunConfigForm
                      selectedSteps={selectedMacroSteps}
                      selectedStarCCMPreset={
                        selectedStarCCMPreset ||
                        ({ name: '', isPreset: false, paramList: [] } as StarCCMParameterList)
                      }
                      selectedMiscPreset={
                        selectedMiscPreset ||
                        ({ name: '', isPreset: false, paramList: [] } as MiscellaneousParameterList)
                      }
                      raceTeamId={currentTeam}
                      customMacros={customMacros}
                      readOnly={
                        submitting ||
                        (mode === 'edit' && existingRun?.overallStatus !== RunStatus.CREATED)
                      }
                      restrictPresets={true}
                      onConfigChange={(config: StarCCMConfig) => {
                        refStarCCMConfig.current = config
                      }}
                    />

                    {/* override section */}
                    <label>Overrides</label>
                    <div className="flex items-start gap-2 w-3/4">
                      <div className="field-box basis-0 grow">
                        <Controller
                          name="coresCount"
                          control={control}
                          defaultValue={0}
                          disabled={submitting}
                          rules={{
                            validate: value => {
                              const coresPerNode = starccmSystem?.coresPerNode
                              if (!coresPerNode || value == null) {
                                return true
                              }
                              return (
                                Number(value) % coresPerNode === 0 ||
                                `Cores Count must be a multiple of ${coresPerNode} (cores/node).`
                              )
                            }
                          }}
                          render={({ field, fieldState }) => (
                            <>
                              <label htmlFor={field.name} className="mb-2 mt-[14px]">
                                Cores Count (cores/node = {starccmSystem?.coresPerNode ?? 'N/A'},
                                max = {starccmSystem?.maxCoresCount ?? 'N/A'}) (starCCM only)
                              </label>
                              <InputNumber
                                id={field.name}
                                value={field.value}
                                onChange={e => {
                                  field.onChange(e.value)
                                }}
                                onKeyDown={e => {
                                  const allowedKeys = [
                                    'Backspace',
                                    'Delete',
                                    'Tab',
                                    'Escape',
                                    'Enter',
                                    'ArrowLeft',
                                    'ArrowRight',
                                    'ArrowUp',
                                    'ArrowDown',
                                    'Home',
                                    'End'
                                  ]
                                  if (e.ctrlKey || e.metaKey || allowedKeys.includes(e.key)) {
                                    return
                                  }
                                  // Cores count is a non-negative integer: block anything that isn't a digit.
                                  if (!/^[0-9]$/.test(e.key)) {
                                    e.preventDefault()
                                  }
                                }}
                                min={0}
                                max={starccmSystem?.maxCoresCount ?? undefined}
                                className={classNames({ error: fieldState.error })}
                                placeholder="Enter number of cores"
                                pt={{
                                  input: {
                                    root: {
                                      className: 'min-w-full'
                                    }
                                  }
                                }}
                              />
                              {getFormErrorMessage(field.name)}
                            </>
                          )}
                        />
                      </div>

                      <div className="field-box  basis-0 grow">
                        <Controller
                          name="walltimeOverride"
                          control={control}
                          defaultValue={0}
                          disabled={submitting}
                          render={({ field, fieldState }) => (
                            <>
                              <label htmlFor={field.name} className="mb-2 mt-[14px]">
                                Walltime Override (preset ={' '}
                                {starccmSystem?.hardwareOverrides?.walltime
                                  ? starccmSystem?.hardwareOverrides?.walltime + 'Hrs'
                                  : 'N/A'}
                                ) (starCCM only)
                              </label>
                              <InputNumber
                                id={field.name}
                                value={field.value}
                                onChange={e => {
                                  field.onChange(e.value)
                                }}
                                onKeyDown={e => {
                                  const allowedKeys = [
                                    'Backspace',
                                    'Delete',
                                    'Tab',
                                    'Escape',
                                    'Enter',
                                    'ArrowLeft',
                                    'ArrowRight',
                                    'ArrowUp',
                                    'ArrowDown',
                                    'Home',
                                    'End'
                                  ]
                                  if (e.ctrlKey || e.metaKey || allowedKeys.includes(e.key)) {
                                    return
                                  }
                                  // Cores count is a non-negative integer: block anything that isn't a digit.
                                  if (!/^[0-9]$/.test(e.key)) {
                                    e.preventDefault()
                                  }
                                }}
                                min={0}
                                className={classNames({ error: fieldState.error })}
                                placeholder="Enter number of cores"
                                pt={{
                                  input: {
                                    root: {
                                      className: 'min-w-full'
                                    }
                                  }
                                }}
                              />
                              {getFormErrorMessage(field.name)}
                            </>
                          )}
                        />
                      </div>
                    </div>
                  </AccordionTab>
                </Accordion>
              </div>
            )
          }
        </form>
      </div>

      {/* Confirmation dialog when geometry files are still uploading */}
      <Modal
        isOpen={showGeoBackgroundModal}
        title="Geometry Files Still Uploading"
        withAction={false}
        showClose={false}
        onCancel={() => setShowGeoBackgroundModal(false)}
        zIndex={50}
        size="md">
        <div className="flex flex-col gap-4">
          <p className="text-gm-black dark:text-white">
            Some Geometry Staging Files or the Geometry Change Image are still uploading to Azure.
            You have two options:
          </p>
          <ul className="list-disc list-inside space-y-2 text-sm text-gm-black dark:text-white">
            <li>
              <strong>Wait for Upload</strong> – stay here until all uploads finish, then save
              normally.
            </li>
            <li>
              <strong>Save &amp; Continue in Background</strong> – save the run metadata now; the
              uploads keep going in the background. A progress indicator will appear at the bottom
              of the screen. Don't close your browser until you see the success notification.
            </li>
          </ul>
          <div className="flex justify-end gap-3 mt-2">
            <Button
              severity="secondary"
              className="text-white"
              onClick={() => setShowGeoBackgroundModal(false)}>
              <span>Wait for Upload</span>
            </Button>
            <Button
              disabled={isSubmittingBackground}
              loading={isSubmittingBackground}
              onClick={() => {
                if (isSubmittingBackground) return
                setIsSubmittingBackground(true)
                setShowGeoBackgroundModal(false)
                saveGeoInBackgroundRef.current = true
                // If sim file is also still uploading, fold it into the background save too
                if (selectedSimFile.some(f => f.uploading)) {
                  saveSimInBackgroundRef.current = true
                }
                handleSubmit(onSubmit, onValidationError)()
              }}>
              {!isSubmittingBackground && <SaveIcon className="fill-white w-4 h-4" />}
              <span className="ml-1">Save &amp; Continue in Background</span>
            </Button>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 text-right">
            Note: saving the run metadata may take up to 10 seconds.
          </p>
        </div>
      </Modal>

      {/* Confirmation dialog when override sim file is still uploading */}
      <Modal
        isOpen={showSimBackgroundModal}
        title="Sim File Still Uploading"
        withAction={false}
        showClose={false}
        onCancel={() => setShowSimBackgroundModal(false)}
        zIndex={50}
        size="sm">
        <div className="flex flex-col gap-4">
          <p className="text-gm-black dark:text-white">
            The Override Sim File is still uploading to Azure. You have two options:
          </p>
          <ul className="list-disc list-inside space-y-2 text-sm text-gm-black dark:text-white">
            <li>
              <strong>Wait for Upload</strong> – stay here until the upload finishes, then save
              normally.
            </li>
            <li>
              <strong>Save &amp; Continue in Background</strong> – save the run metadata now; the
              upload keeps going in the background. A progress indicator will appear in the
              notification bell. Don’t close your browser until you see the success notification.
            </li>
          </ul>
          <div className="flex justify-end gap-3 mt-2">
            <Button
              severity="secondary"
              className="text-white"
              onClick={() => setShowSimBackgroundModal(false)}>
              <span>Wait for Upload</span>
            </Button>
            <Button
              disabled={isSubmittingBackground}
              loading={isSubmittingBackground}
              onClick={() => {
                if (isSubmittingBackground) return
                setIsSubmittingBackground(true)
                setShowSimBackgroundModal(false)
                saveSimInBackgroundRef.current = true
                handleSubmit(onSubmit, onValidationError)()
              }}>
              {!isSubmittingBackground && <SaveIcon className="fill-white w-4 h-4" />}
              <span className="ml-1">Save &amp; Continue in Background</span>
            </Button>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 text-right">
            Note: saving the run metadata may take up to 10 seconds.
          </p>
        </div>
      </Modal>

      {/* Confirmation dialog when user closes modal while files are still uploading */}
      <Modal
        isOpen={showCloseConfirmModal}
        title="Upload In Progress"
        withAction={false}
        showClose={false}
        onCancel={() => setShowCloseConfirmModal(false)}
        zIndex={50}
        size="sm">
        <div className="flex flex-col gap-4">
          <p className="text-gm-black dark:text-white">
            A file is still uploading. Are you sure you want to close? This will cancel the upload.
          </p>
          <div className="flex justify-end gap-3 mt-2">
            <Button
              severity="secondary"
              className="text-white"
              onClick={() => setShowCloseConfirmModal(false)}>
              <span>Continue Uploading</span>
            </Button>
            <Button severity="danger" onClick={handleConfirmClose}>
              <span>Stop Upload &amp; Close</span>
            </Button>
          </div>
        </div>
      </Modal>
    </Modal>
  )
}

export default SubmissionModal
